import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { apiService, File } from '../services/api';
import { usePageTitle } from '../hooks/usePageTitle';
import { useUser } from '../contexts/UserContext';
import { useAuth } from '../contexts/AuthContext';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { usePasskeyChallenge } from '../hooks/usePasskeyChallenge';
import { ViewMode } from '../components/Layout/ModeSelector';
import DateTimePicker from '../components/DateTimePicker';
import {
  DocumentIcon,
  UserIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  DocumentArrowUpIcon,
  PencilIcon,
  CogIcon,
  LockClosedIcon,
  LockOpenIcon,
  ShieldExclamationIcon,
  ClockIcon,
  HashtagIcon,
  TagIcon,
  PaperAirplaneIcon,
  EyeIcon,
  FunnelIcon,
  UserGroupIcon,
  MinusIcon,
  ShieldCheckIcon,
  ArrowsRightLeftIcon,
  EllipsisVerticalIcon,
  Squares2X2Icon,
  Bars3Icon,
  ArrowDownTrayIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import {
  Squares2X2Icon as Squares2X2IconSolid,
  Bars3Icon as Bars3IconSolid,
} from '@heroicons/react/24/solid';
import GlobalSearchModal from '../components/GlobalSearchModal';
import { useTranslation } from 'react-i18next';

const Assets: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation('assets');
  const [searchParams, setSearchParams] = useSearchParams();
  // Initialize filter state from URL so first paint has correct values (e.g. from Users "x assets" link)
  const [searchTerm, setSearchTerm] = useState(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('searchTerm') || '';
  });
  const [emailSearch, setEmailSearch] = useState(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('emailSearch') || '';
  });
  const [tags, setTags] = useState<string[]>(() => {
    if (typeof window === 'undefined') return [];
    const p = new URLSearchParams(window.location.search).get('tags');
    return p ? (() => { try { return JSON.parse(p); } catch { return []; } })() : [];
  });
  const [assetType, setAssetType] = useState<'file' | 'folder' | undefined>(() => {
    if (typeof window === 'undefined') return undefined;
    const p = new URLSearchParams(window.location.search).get('assetType');
    return (p === 'file' || p === 'folder') ? p : undefined;
  });
  const [dateFrom, setDateFrom] = useState<string | undefined>(() => {
    if (typeof window === 'undefined') return undefined;
    return new URLSearchParams(window.location.search).get('dateFrom') || undefined;
  });
  const [dateTo, setDateTo] = useState<string | undefined>(() => {
    if (typeof window === 'undefined') return undefined;
    return new URLSearchParams(window.location.search).get('dateTo') || undefined;
  });
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [selectedTab, setSelectedTab] = useState<'my-assets' | 'shared-with-me' | 'requests'>(() => {
    // Reader mode defaults to shared-with-me; others use localStorage or my-assets
    const savedTab = localStorage.getItem('assets-selected-tab');
    return (savedTab as 'my-assets' | 'shared-with-me' | 'requests') || 'my-assets';
  });
  const [viewMode, setViewMode] = useState<'list' | 'grid'>(() => {
    // Load saved view mode from localStorage, default to 'list'
    const savedViewMode = localStorage.getItem('assets-view-mode');
    return (savedViewMode as 'list' | 'grid') || 'list';
  });
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [showAddRecipients, setShowAddRecipients] = useState(false);
  const [showRemoveRecipients, setShowRemoveRecipients] = useState(false);
  const [newRecipient, setNewRecipient] = useState('');
  const [csvData, setCsvData] = useState('');
  const [isAddingRecipient, setIsAddingRecipient] = useState(false);
  const [showChangePolicy, setShowChangePolicy] = useState(false);
  const [selectedPolicyId, setSelectedPolicyId] = useState<string>('');
  const [selectedFolderPolicyId, setSelectedFolderPolicyId] = useState<string>('');
  const [overwriteRecipients, setOverwriteRecipients] = useState(false);
  const [isChangingPolicy, setIsChangingPolicy] = useState(false);
  const [showChangeOwner, setShowChangeOwner] = useState(false);
  const [newOwnerEmail, setNewOwnerEmail] = useState('');
  const [isChangingOwner, setIsChangingOwner] = useState(false);
  const [recipientsToAdd, setRecipientsToAdd] = useState<string[]>([]);
  const [recipientsToRemove, setRecipientsToRemove] = useState<string[]>([]);
  const [isProcessingRecipients] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<'name' | 'datetime' | 'recipients'>('datetime');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [showUnblockConfirm, setShowUnblockConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSingleToggleConfirm, setShowSingleToggleConfirm] = useState(false);
  const [singleToggleFile, setSingleToggleFile] = useState<{ id: string; status: string; name: string } | null>(null);
  const [isBlockingFiles, setIsBlockingFiles] = useState(false);
  const [isDeletingFiles, setIsDeletingFiles] = useState(false);
  const [showRequestAccessModal, setShowRequestAccessModal] = useState(false);
  const [selectedFileForRequest, setSelectedFileForRequest] = useState<{ id: string; name: string } | null>(null);
  const [requestType, setRequestType] = useState<'access_count_limit' | 'access_period_limit'>('access_count_limit');
  const [requestReason, setRequestReason] = useState('');
  const [isCreatingRequest, setIsCreatingRequest] = useState(false);
  const [isExportingKeys, setIsExportingKeys] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedFileForShare, setSelectedFileForShare] = useState<{ id: string; name: string } | null>(null);
  const [shareRecipientEmail, setShareRecipientEmail] = useState('');
  const [isAddingShareRecipient, setIsAddingShareRecipient] = useState(false);

  // Passkey challenge hook for access blocking
  const accessBlockingChallenge = usePasskeyChallenge('access_blocking');
  const [showManageTags, setShowManageTags] = useState(false);
  const [tagOperation, setTagOperation] = useState<'add' | 'remove'>('add');
  const [tagInput, setTagInput] = useState('');
  const [tagsToManage, setTagsToManage] = useState<string[]>([]);
  const [isManagingTags, setIsManagingTags] = useState(false);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  const [isSearchingTags, setIsSearchingTags] = useState(false);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState<number>(-1);
  const [selectedAssetsTags, setSelectedAssetsTags] = useState<Set<string>>(new Set());
  const [, setIsLoadingAssetTags] = useState(false);
  const tagInputRef = useRef<HTMLInputElement>(null);
  const [openFileMenu, setOpenFileMenu] = useState<string | null>(null);
  const { profile } = useUser();
  const { user: currentUser } = useAuth();
  const permissions = useRolePermissions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // View mode (admin vs sysadmin) - must be before files useQuery so we can pass view_mode to API
  const [sidebarViewMode, setSidebarViewMode] = React.useState<ViewMode>(() => {
    try {
      const saved = localStorage.getItem('view_mode');
      if (saved) {
        const mode = saved as ViewMode;
        if (currentUser?.role === 'sysadmin') return mode;
        if (currentUser?.role === 'admin') return mode === 'sysadmin' ? 'admin' : mode;
        return 'user';
      }
      if (currentUser?.role === 'sysadmin') return 'sysadmin';
      if (currentUser?.role === 'admin') return 'admin';
      return 'user';
    } catch {
      return 'user';
    }
  });
  React.useEffect(() => {
    const handler = (event: CustomEvent) => {
      if (event.detail.viewMode) setSidebarViewMode(event.detail.viewMode);
      // Only apply legacy event for non-sysadmins: it only has admin vs user; for sysadmin we need admin vs sysadmin from view-mode-changed
      else if (event.detail.isAdministrationView !== undefined && currentUser?.role !== 'sysadmin') setSidebarViewMode(event.detail.isAdministrationView ? 'admin' : 'user');
    };
    window.addEventListener('view-mode-changed', handler as EventListener);
    window.addEventListener('administration-view-changed', handler as EventListener);
    return () => {
      window.removeEventListener('view-mode-changed', handler as EventListener);
      window.removeEventListener('administration-view-changed', handler as EventListener);
    };
  }, [currentUser?.role]);
  const effectiveMode: ViewMode = currentUser?.role === 'sysadmin' ? sidebarViewMode : (currentUser?.role === 'admin' ? (sidebarViewMode === 'sysadmin' ? 'admin' : sidebarViewMode) : 'user');
  // When company admin lands with owner filter (e.g. from Users "x assets" link), fetch company scope so we get data to filter; otherwise "user" mode returns 0 files and list is empty
  const effectiveModeForFiles: ViewMode =
    currentUser?.role === 'admin' && emailSearch?.trim()
      ? 'admin'
      : effectiveMode;

  // Helper function to check if user can perform actions on a file
  const canPerformActions = (file: any): boolean => {
    if (!profile || !currentUser) return false;
    
    // Sysadmin can always perform actions
    if (currentUser.role === 'sysadmin') return true;
    
    // Check if user is the owner (by ID or email)
    if (file.owner_id === profile.id || file.user_id === profile.id || 
        file.owner_email?.toLowerCase() === profile.email?.toLowerCase()) {
      return true;
    }
    
    // Check if user is admin of the owner's company
    // Try to get owner_company_id from file, or check if owner_email matches current user's company users
    if (currentUser.role === 'admin') {
      // If file has owner_company_id, check if it matches
      if (file.owner_company_id && file.owner_company_id === currentUser.company_id) {
        return true;
      }
      // For shared-with-me tab, if we're admin and the file owner is in our company, we can manage it
      // This will be properly enforced on backend, but for UI we allow it
      if (selectedTab === 'shared-with-me' && file.owner_company_id === currentUser.company_id) {
        return true;
      }
    }
    
    return false;
  };

  // State for selected tabs from search modal
  const [searchSelectedTabs, setSearchSelectedTabs] = useState<Set<'my-assets' | 'shared-with-me' | 'requests'>>(
    new Set<'my-assets' | 'shared-with-me' | 'requests'>(['my-assets'])
  );

  // Parse selected tabs from URL params
  useEffect(() => {
    const selectedTabsParam = searchParams.get('selectedTabs');
    if (selectedTabsParam) {
      const tabs: Array<'my-assets' | 'shared-with-me' | 'requests'> = selectedTabsParam.split(',').filter((t): t is 'my-assets' | 'shared-with-me' | 'requests' => 
        t === 'my-assets' || t === 'shared-with-me' || t === 'requests'
      );
      if (tabs.length > 0) {
        setSearchSelectedTabs(new Set<'my-assets' | 'shared-with-me' | 'requests'>(tabs));
      }
    }
  }, [searchParams]);

  // Sync URL ?tab= to selectedTab (e.g. /assets?tab=shared-with-me)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'shared-with-me' || tabParam === 'my-assets' || tabParam === 'requests') {
      setSelectedTab(tabParam);
    }
  }, [searchParams]);

  // Reader mode: default to Shared with me when opening /assets without tab (or with my-assets)
  useEffect(() => {
    if (currentUser?.mode !== 'reader') return;
    const tabParam = searchParams.get('tab');
    if (tabParam === 'shared-with-me' || tabParam === 'requests') return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'shared-with-me');
      return next;
    }, { replace: true });
    setSelectedTab('shared-with-me');
  }, [currentUser?.mode, searchParams, setSearchParams]);

  // Read URL params from global search and apply to filters
  const initialFilters = useMemo(() => {
    const searchTermParam = searchParams.get('searchTerm');
    const emailSearchParam = searchParams.get('emailSearch');
    const tagsParam = searchParams.get('tags');
    const assetTypeParam = searchParams.get('assetType');
    const dateFromParam = searchParams.get('dateFrom');
    const dateToParam = searchParams.get('dateTo');

    // Only create filters if at least one param has a non-empty value
    if (!searchTermParam && !emailSearchParam && !tagsParam && !assetTypeParam && !dateFromParam && !dateToParam) {
      return undefined;
    }

    return {
      searchTerm: searchTermParam || undefined,
      emailSearch: emailSearchParam || undefined,
      tags: tagsParam ? JSON.parse(tagsParam) : undefined,
      assetType: (assetTypeParam as 'file' | 'folder') || undefined,
      dateFrom: dateFromParam || undefined,
      dateTo: dateToParam || undefined
    };
  }, [searchParams]);

  // Apply initial filters when they change (e.g. from global search or Users "x assets" link)
  useEffect(() => {
    if (initialFilters) {
      console.log('🔄 Assets: Applying URL filters:', initialFilters);
      // Only set state if the value is defined (not undefined)
      if (initialFilters.searchTerm !== undefined) setSearchTerm(initialFilters.searchTerm || '');
      if (initialFilters.emailSearch !== undefined) setEmailSearch(initialFilters.emailSearch || '');
      if (initialFilters.tags !== undefined) setTags(initialFilters.tags || []);
      if (initialFilters.assetType !== undefined) setAssetType(initialFilters.assetType);
      if (initialFilters.dateFrom !== undefined) setDateFrom(initialFilters.dateFrom);
      if (initialFilters.dateTo !== undefined) setDateTo(initialFilters.dateTo);

      // Clear URL params but preserve emailSearch so refresh keeps owner filter (e.g. from Users asset link)
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('searchTerm');
        next.delete('tags');
        next.delete('assetType');
        next.delete('dateFrom');
        next.delete('dateTo');
        const keepEmail = next.get('emailSearch');
        next.delete('emailSearch');
        if (keepEmail) next.set('emailSearch', keepEmail);
        return next;
      });

      // Invalidate query to ensure it refetches with new filters
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['files'] });
      }, 0);
    }
  }, [initialFilters, setSearchParams, queryClient]);
  // Requests tab state
  const [requestsSubTab, setRequestsSubTab] = useState<'received' | 'sent'>('received');
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [isResponding, setIsResponding] = useState(false);
  const [responseStatus, setResponseStatus] = useState<'accepted' | 'rejected'>('accepted');
  const [responseMessage, setResponseMessage] = useState('');
  const [overrideTimesAccessible, setOverrideTimesAccessible] = useState<string>('');
  const [overrideAllowedAccessTo, setOverrideAllowedAccessTo] = useState<string>('');
  const [isUnlimitedPeriod, setIsUnlimitedPeriod] = useState<boolean>(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [requestToCancel, setRequestToCancel] = useState<string | null>(null);
  const [isCanceling, setIsCanceling] = useState(false);
  const [showRejectConfirmModal, setShowRejectConfirmModal] = useState(false);
  const [requestToReject, setRequestToReject] = useState<any>(null);
  const [isRejecting, setIsRejecting] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  // Set page title from locale
  usePageTitle(t('title'), t('subtitle'));

  const { data: filesData, isLoading, error } = useQuery({
    queryKey: ['files', searchTerm, emailSearch, tags, assetType, dateFrom, dateTo, effectiveModeForFiles],
    queryFn: () => apiService.getFiles({
      searchTerm,
      emailSearch,
      tags,
      assetType,
      dateFrom,
      dateTo,
      viewMode: effectiveModeForFiles,
    }),
    placeholderData: (previousData) => previousData, // Keep previous data visible while fetching new data to prevent flickering
  });

  // When view mode (for files request) changes, force refetch so we don't show stale data
  const prevEffectiveModeRef = useRef<ViewMode | null>(null);
  useEffect(() => {
    if (prevEffectiveModeRef.current !== null && prevEffectiveModeRef.current !== effectiveModeForFiles) {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      queryClient.invalidateQueries({ queryKey: ['shared-files'] });
    }
    prevEffectiveModeRef.current = effectiveModeForFiles;
  }, [effectiveModeForFiles, queryClient]);

  // Save current filters to localStorage for GlobalSearchModal to read
  useEffect(() => {
    const isAssetsPage = location.pathname === '/assets';
    if (!isAssetsPage) {
      // Clear filters when navigating away
      localStorage.removeItem('assets_currentFilters');
      return;
    }

    const hasFilters = searchTerm || emailSearch || tags.length > 0 || assetType || dateFrom || dateTo;
    if (hasFilters) {
      const modalFilters = {
        assetName: searchTerm,
        emailSearch: emailSearch,
        assetType: assetType,
        assetTags: tags,
        dateFrom: dateFrom,
        dateTo: dateTo,
      };
      localStorage.setItem('assets_currentFilters', JSON.stringify(modalFilters));
    } else {
      // Clear if no filters
      localStorage.removeItem('assets_currentFilters');
    }
  }, [searchTerm, emailSearch, tags, assetType, dateFrom, dateTo, location.pathname]);

  const { data: sharedFilesData, isLoading: sharedFilesLoading, error: sharedFilesError, refetch: refetchSharedFiles } = useQuery({
    queryKey: ['shared-files'],
    queryFn: () => apiService.getSharedWithMeFiles(),
    enabled: false, // Don't run automatically - only when needed
  });

  // Fetch policies for policy change functionality (lazy load - only when modal opens)
  const { data: policiesData, isLoading: policiesLoading, refetch: refetchPolicies } = useQuery({
    queryKey: ['policies'],
    queryFn: () => apiService.getPolicies(),
    enabled: false, // Don't fetch automatically - only when Change Policy modal opens
  });

  // Fetch received requests (all statuses) - always fetch to show pending count
  const { data: receivedRequestsData, isLoading: receivedRequestsLoading } = useQuery({
    queryKey: ['access-requests', 'received'],
    queryFn: () => apiService.getAllReceivedAccessRequests(),
    enabled: true, // Always fetch for pending count
  });

  // Fetch sent requests
  const { data: sentRequestsData, isLoading: sentRequestsLoading } = useQuery({
    queryKey: ['access-requests', 'my-requests'],
    queryFn: () => apiService.getMyAccessRequests(),
    enabled: selectedTab === 'requests' && requestsSubTab === 'sent',
  });

  // Check if there are pending requests (for orange dot indicator)
  const hasPendingRequests = useMemo(() => {
    if (!receivedRequestsData?.requests) return false;
    return receivedRequestsData.requests.some((r: any) => r.status === 'pending');
  }, [receivedRequestsData]);

  // Save tab selection to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('assets-selected-tab', selectedTab);
  }, [selectedTab]);

  // Save view mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('assets-view-mode', viewMode);
  }, [viewMode]);

  // Collapse request detail when switching away from requests tab
  useEffect(() => {
    if (selectedTab !== 'requests') {
      setShowReviewModal(false);
      setSelectedRequest(null);
      setOpenDropdownId(null);
    }
  }, [selectedTab]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (openDropdownId && !target.closest('.dropdown-menu-container')) {
        setOpenDropdownId(null);
      }
    };
    if (openDropdownId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openDropdownId]);

  const getRequestTypeLabel = (type: string) => {
    switch (type) {
      case 'access_count_limit':
        return 'Access Count Limit';
      case 'access_period_limit':
        return 'Access Period Limit';
      case 'asset_permissions':
        return 'Asset Permissions';
      default:
        return type;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            Pending
          </span>
        );
      case 'accepted':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Accepted
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Rejected
          </span>
        );
      case 'canceled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Canceled
          </span>
        );
      default:
        return null;
    }
  };

  const handleViewDetails = async (request: any) => {
    try {
      const detailedRequest = await apiService.getAccessRequest(request.id);
      setSelectedRequest(detailedRequest);
      setShowReviewModal(true);
      // Reset form
      setResponseStatus('accepted');
      setResponseMessage('');
      setOverrideTimesAccessible('');

      // Initialize overrideAllowedAccessTo with current value if it exists
      if (detailedRequest.current_allowed_access_to) {
        // Convert to yyyy-mm-dd hh:mm:ss format for display
        const date = new Date(detailedRequest.current_allowed_access_to);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        setOverrideAllowedAccessTo(`${year}-${month}-${day} ${hours}:${minutes}:${seconds}`);
        setIsUnlimitedPeriod(false);
      } else {
        // No time limit (unlimited) - set toggle ON
        setOverrideAllowedAccessTo('');
        setIsUnlimitedPeriod(true);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to load request details');
    }
  };

  const handleRespond = async (status: 'accepted' | 'rejected') => {
    if (!selectedRequest) return;

    setIsResponding(true);
    setResponseStatus(status);
    try {
      let timesAccessible: number | null | undefined = undefined;
      let allowedAccessTo: string | null | undefined = undefined;

      // Handle allowedAccessTo for access_period_limit requests
      if (status === 'accepted' && selectedRequest.request_type === 'access_period_limit') {
        if (isUnlimitedPeriod) {
          // Toggle is ON - set to null (unlimited)
          allowedAccessTo = null;
        } else if (overrideAllowedAccessTo) {
          // Convert yyyy-mm-dd hh:mm:ss to RFC3339 format for backend
          const dateTimeStr = overrideAllowedAccessTo.replace(' ', 'T');
          const date = new Date(dateTimeStr);
          if (!isNaN(date.getTime())) {
            allowedAccessTo = date.toISOString();
          } else {
            allowedAccessTo = undefined; // Invalid date, use requested value
          }
        } else {
          // No override - use requested value (undefined)
          allowedAccessTo = undefined;
        }
      }

      // If accepting an access_count_limit request without override, set to null (unlimited)
      if (status === 'accepted' && selectedRequest.request_type === 'access_count_limit') {
        if (overrideTimesAccessible) {
          timesAccessible = parseInt(overrideTimesAccessible);
        } else {
          // No override set - set to null to make it unlimited
          timesAccessible = null;
        }
      } else if (overrideTimesAccessible) {
        timesAccessible = parseInt(overrideTimesAccessible);
      }

      await apiService.respondToAccessRequest(
        selectedRequest.id,
        status,
        responseMessage || undefined,
        timesAccessible,
        allowedAccessTo
      );

      toast.success(`Request ${status === 'accepted' ? 'accepted' : 'rejected'} successfully`);
      setShowReviewModal(false);
      setSelectedRequest(null);

      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['access-requests'] });
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to respond to request');
    } finally {
      setIsResponding(false);
    }
  };

  const handleCancelClick = (requestId: string) => {
    setRequestToCancel(requestId);
    setShowCancelConfirmModal(true);
  };

  const handleCancelConfirm = async () => {
    if (!requestToCancel) return;

    setIsCanceling(true);
    try {
      await apiService.cancelAccessRequest(requestToCancel);
      toast.success('Access request canceled successfully');
      queryClient.invalidateQueries({ queryKey: ['access-requests'] });
      setShowCancelConfirmModal(false);
      setRequestToCancel(null);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to cancel access request');
    } finally {
      setIsCanceling(false);
    }
  };

  const handleQuickRejectClick = (request: any) => {
    setRequestToReject(request);
    setShowRejectConfirmModal(true);
  };

  const handleRejectConfirm = async () => {
    if (!requestToReject) return;

    setIsRejecting(true);
    try {
      await apiService.respondToAccessRequest(
        requestToReject.id,
        'rejected',
        undefined,
        undefined,
        undefined
      );

      toast.success('Request rejected successfully');
      queryClient.invalidateQueries({ queryKey: ['access-requests'] });
      setShowRejectConfirmModal(false);
      setRequestToReject(null);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to reject request');
    } finally {
      setIsRejecting(false);
    }
  };


  // Admin/sysadmin: only my-assets tab exists; switch away from shared-with-me/requests if mode changed
  useEffect(() => {
    if ((effectiveMode === 'admin' || effectiveMode === 'sysadmin') && (selectedTab === 'shared-with-me' || selectedTab === 'requests')) {
      setSelectedTab('my-assets');
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', 'my-assets');
        return next;
      }, { replace: true });
    }
  }, [effectiveMode, selectedTab, setSearchParams]);

  // Trigger shared files query when "Shared with me" tab is selected (or when mode changes while on tab)
  useEffect(() => {
    if (selectedTab === 'shared-with-me') {
      refetchSharedFiles();
    }
  }, [selectedTab, effectiveMode, refetchSharedFiles]);

  // Clear selected items when switching tabs
  useEffect(() => {
    setSelectedItems(new Set());
  }, [selectedTab]);



  // Tag search with debouncing
  useEffect(() => {
    const searchTimeout = setTimeout(async () => {
      if (tagInput.trim().length > 0) {
        setIsSearchingTags(true);
        try {
          const response = await apiService.searchTags(tagInput.trim(), 20);

          let filteredSuggestions = response.tags.filter(
            (tag: string) => !tagsToManage.includes(tag)
          );

          // Apply different filtering based on operation mode
          if (tagOperation === 'add') {
            // For "Add Tags": Filter out tags that the selected assets already have
            filteredSuggestions = filteredSuggestions.filter(
              (tag: string) => !selectedAssetsTags.has(tag)
            );
          } else {
            // For "Remove Tags": Only show tags that at least one selected asset has
            filteredSuggestions = filteredSuggestions.filter(
              (tag: string) => selectedAssetsTags.has(tag)
            );
          }

          setTagSuggestions(filteredSuggestions);
          setShowTagDropdown(filteredSuggestions.length > 0);
          setSelectedSuggestionIndex(-1); // Reset selection when suggestions change
        } catch (error) {
          console.error('Failed to search tags:', error);
          setTagSuggestions([]);
          setShowTagDropdown(false);
          setSelectedSuggestionIndex(-1);
        } finally {
          setIsSearchingTags(false);
        }
      } else {
        setTagSuggestions([]);
        setShowTagDropdown(false);
        setSelectedSuggestionIndex(-1);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [tagInput, tagsToManage, tagOperation, selectedAssetsTags]);

  // Fetch tags for selected assets when modal opens
  useEffect(() => {
    const fetchSelectedAssetTags = async () => {
      if (!showManageTags || selectedItems.size === 0) {
        setSelectedAssetsTags(new Set());
        return;
      }

      setIsLoadingAssetTags(true);
      try {
        const allTags = new Set<string>();
        const assetIds = Array.from(selectedItems);

        // Fetch tags for each selected asset
        await Promise.all(
          assetIds.map(async (assetId) => {
            try {
              const response = await apiService.getAssetTags(assetId);
              response.tags.forEach((tag: any) => allTags.add(tag.tag_name));
            } catch (error) {
              console.error(`Failed to fetch tags for asset ${assetId}:`, error);
            }
          })
        );

        setSelectedAssetsTags(allTags);
      } catch (error) {
        console.error('Failed to fetch asset tags:', error);
        setSelectedAssetsTags(new Set());
      } finally {
        setIsLoadingAssetTags(false);
      }
    };

    fetchSelectedAssetTags();
  }, [showManageTags, selectedItems]);

  // Close tag dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check if click is outside the input AND outside the dropdown
      if (tagInputRef.current && !tagInputRef.current.contains(target)) {
        // Find the dropdown element
        const dropdown = document.querySelector('.tag-suggestions-dropdown');
        if (dropdown && !dropdown.contains(target)) {
          setShowTagDropdown(false);
        } else if (!dropdown) {
          setShowTagDropdown(false);
        }
      }
    };

    if (showTagDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTagDropdown]);

  // ESC key handler for Add Recipients modal
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showAddRecipients) {
        setShowAddRecipients(false);
        setNewRecipient('');
        setCsvData('');
        setRecipientsToAdd([]);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showAddRecipients]);

  // ESC key handler for Remove Recipients modal
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showRemoveRecipients) {
        setShowRemoveRecipients(false);
        setNewRecipient('');
        setCsvData('');
        setRecipientsToRemove([]);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showRemoveRecipients]);

  // Fetch policies when Change Policy modal opens
  useEffect(() => {
    if (showChangePolicy && !policiesData) {
      console.log('🔄 Lazy loading policies for Change Policy modal...');
      refetchPolicies();
    }
  }, [showChangePolicy, policiesData, refetchPolicies]);

  // ESC key handler for Change Policy modal
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showChangePolicy) {
        setShowChangePolicy(false);
        setSelectedPolicyId('');
        setSelectedFolderPolicyId('');
        setOverwriteRecipients(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showChangePolicy]);

  // ESC key handler for Change Owner modal
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showChangeOwner) {
        setShowChangeOwner(false);
        setNewOwnerEmail('');
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showChangeOwner]);

  // Helper function to format file name (remove .zip extension for folders)
  const formatFileName = (fileName: string, assetType?: string, mimeType?: string): string => {
    // If this is a folder (by asset_type or mime_type), remove .zip extension from display
    if (assetType?.toLowerCase() === 'folder' || mimeType === 'application/zip') {
      if (fileName.toLowerCase().endsWith('.zip')) {
        return fileName.slice(0, -4); // Remove .zip extension
      }
    }
    return fileName;
  };

  const getFileIcon = (fileName: string, assetType?: string, mimeType?: string) => {
    // Check if this is a folder (prioritize asset_type, then mime_type)
    // If asset_type is 'folder', always show folder icon (for zipped folders stored as files)
    // If mime_type is 'application/zip', show folder icon (zipped folders - handles legacy files with asset_type="file")
    if (assetType?.toLowerCase() === 'folder' || mimeType === 'application/zip') {
      return (
        <img
          src="/asset-icons/folder.webp"
          alt="folder"
          className="w-8 h-8 flex-shrink-0 object-contain"
        />
      );
    }

    const extension = fileName.split('.').pop()?.toLowerCase() || 'file';
    const upperExtension = extension.toUpperCase();

    // Map extensions to icon file names (handles variations like docx -> doc)
    const extensionMap: Record<string, string> = {
      'txt': 'txt',
      'doc': 'doc',
      'docx': 'doc',
      'xls': 'xls',
      'xlsx': 'xls',
      'ppt': 'ppt',
      'pptx': 'ppt',
      'pdf': 'pdf',
      'csv': 'csv',
      'png': 'png',
      'jpg': 'jpg',
      'jpeg': 'jpg',
    };

    // Check if we have an icon for this extension
    const iconName = extensionMap[extension];
    if (iconName) {
      return (
        <img
          src={`/asset-icons/${iconName}.webp`}
          alt={`${extension} file`}
          className="w-8 h-8 flex-shrink-0 object-contain"
        />
      );
    }

    // Fallback to text-based icon for unsupported types
    return (
      <div className="w-8 h-8 flex-shrink-0 bg-black dark:bg-gray-800 rounded flex items-center justify-center">
        <span className="text-white text-xs font-bold">{upperExtension}</span>
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    // Returns yyyy-mm-dd HH:MM:SS format
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const filteredFiles = useMemo(() => {
    console.log('🔍 MEMO: Starting filter - emailSearch:', `"${emailSearch}"`, 'Total files:', selectedTab === 'shared-with-me' ? sharedFilesData?.files?.length : filesData?.files?.length);
    
    // Check if current tab should have filters applied
    const shouldApplyFilters = searchSelectedTabs.has(selectedTab);
    const hasFilters = searchTerm || emailSearch || tags.length > 0 || assetType || dateFrom || dateTo;
    
    // If filters exist but this tab is not selected, return empty array
    if (hasFilters && !shouldApplyFilters) {
      return [];
    }

    if (selectedTab === 'shared-with-me') {
      // Use shared files data for "Shared with me" tab.
      // API already returns files shared with the current user (or company in admin mode); do not filter by owner in user mode.
      if (!sharedFilesData?.files) return [];

      const filesToFilter = sharedFilesData.files;

      return filesToFilter.filter((file: any) => {
        // Search term filter
        const matchesSearch = searchTerm.trim() === '' || file.file_name.toLowerCase().includes(searchTerm.toLowerCase());

        // Email filter - for "Shared with me" tab, primarily filter by owner email
        let matchesEmail = true;
        if (emailSearch && emailSearch.trim() !== '') {
          const searchEmail = emailSearch.toLowerCase().trim();
          console.log(`🔍 MEMO: Filtering file "${file.file_name}" with search email: "${searchEmail}"`);

          // For "Shared with me" tab, owner email is the primary identifier
          const hasOwnerMatch = file.owner_email?.toLowerCase().includes(searchEmail);
          if (hasOwnerMatch) {
            console.log(`  ✓ Found owner match: "${file.owner_email}" includes "${searchEmail}"`);
          }

          // Also check recipient emails as a secondary match
          const hasRecipientMatch = file.recipients?.some((recipient: any) => {
            const recipientEmail = recipient.value?.toLowerCase();
            const matches = recipientEmail?.includes(searchEmail);
            if (matches) {
              console.log(`  ✓ Found recipient match: "${recipientEmail}" includes "${searchEmail}"`);
            }
            return matches;
          });

          matchesEmail = hasOwnerMatch || hasRecipientMatch || false;
          console.log(`  → Result: ${matchesEmail ? 'MATCH' : 'NO MATCH'}`);
        }

        // Asset type filter
        const matchesAssetType = assetType ?
          (file.asset_type === assetType) : true;

        // Date range filter (using shared_at for shared files)
        const fileDate = new Date(file.shared_at || file.created_at);
        const matchesDateFrom = dateFrom ? fileDate >= new Date(dateFrom) : true;
        const matchesDateTo = dateTo ? fileDate <= new Date(dateTo) : true;

        return matchesSearch && matchesEmail && matchesAssetType && matchesDateFrom && matchesDateTo;
      });
    } else {
      // Use regular files data for other tabs
      if (!filesData?.files || !profile) return [];

      let filesToFilter = filesData.files;

      // In user mode, filter to show only user's own files
      if (effectiveMode === 'user' && currentUser && (currentUser.role === 'admin' || currentUser.role === 'sysadmin')) {
        filesToFilter = filesToFilter.filter((file: File) => {
          return file.user_id === profile.id || 
                 file.owner_email?.toLowerCase() === profile.email?.toLowerCase();
        });
      }
      // In admin mode: show company assets (for company admins) or all assets (for sysadmins in admin mode)
      // In sysadmin mode: show all assets from all companies

      return filesToFilter.filter((file: File) => {
        // Search term filter
        const matchesSearch = searchTerm.trim() === '' || file.file_name.toLowerCase().includes(searchTerm.toLowerCase());

        // Email filter - search both recipient emails and owner email
        let matchesEmail = true;
        if (emailSearch && emailSearch.trim() !== '') {
          const searchEmail = emailSearch.toLowerCase().trim();
          console.log(`🔍 File: "${file.file_name}" (ID: ${file.id.substring(0, 8)}...) Owner: "${file.owner_email}" | Recipients: ${file.recipients?.length || 0}`);

          const hasRecipientMatch = file.recipients?.some((recipient: any) => {
            const recipientEmail = recipient.value?.toLowerCase();
            const matches = recipientEmail?.includes(searchEmail);
            if (matches) {
              console.log(`  ✅ RECIPIENT MATCH: "${recipientEmail}" contains "${searchEmail}"`);
            }
            return matches;
          });

          // Owner filter: allow when canViewAllLogs (sysadmin) or when in admin/sysadmin view (company admin viewing "this user's assets")
          const canFilterByOwner = permissions.canViewAllLogs || effectiveMode === 'admin' || effectiveMode === 'sysadmin';
          const hasOwnerMatch = canFilterByOwner && file.owner_email?.toLowerCase().includes(searchEmail);
          if (hasOwnerMatch) {
            console.log(`  ✅ OWNER MATCH: "${file.owner_email}" contains "${searchEmail}"`);
          }

          matchesEmail = hasRecipientMatch || hasOwnerMatch || false;
          console.log(`  → ${matchesEmail ? '✅✅✅ INCLUDE IN RESULTS' : '❌ EXCLUDE FROM RESULTS'}`);
        }

        // Asset type filter
        const matchesAssetType = assetType ?
          (file.asset_type === assetType) : true;

        // Date range filter (using created_at)
        const fileDate = new Date(file.created_at);
        const matchesDateFrom = dateFrom ? fileDate >= new Date(dateFrom) : true;
        const matchesDateTo = dateTo ? fileDate <= new Date(dateTo) : true;

        return matchesSearch && matchesEmail && matchesAssetType && matchesDateFrom && matchesDateTo;
      });
    }
  }, [filesData?.files, sharedFilesData?.files, searchTerm, emailSearch, tags, assetType, dateFrom, dateTo, selectedTab, profile, permissions.canViewAllLogs, searchSelectedTabs, currentUser, effectiveMode]);

  // Log filter results summary
  useEffect(() => {
    if (emailSearch && emailSearch.trim() !== '') {
      console.log(`📊 FILTER SUMMARY: Showing ${filteredFiles.length} files matching "${emailSearch}" out of ${selectedTab === 'shared-with-me' ? sharedFilesData?.files?.length : filesData?.files?.length} total files`);
    }
  }, [filteredFiles.length, emailSearch, selectedTab, sharedFilesData?.files?.length, filesData?.files?.length]);

  // Sort files by selected field
  const sortedFiles = useMemo(() => {
    return [...filteredFiles].sort((a, b) => {
      let comparison = 0;
      
      if (sortField === 'name') {
        const nameA = (a as any).file_name?.toLowerCase() || '';
        const nameB = (b as any).file_name?.toLowerCase() || '';
        comparison = nameA.localeCompare(nameB);
      } else if (sortField === 'datetime') {
      // Handle both File and SharedFile types
      const dateA = new Date((a as any).created_at || (a as any).shared_at).getTime();
      const dateB = new Date((b as any).created_at || (b as any).shared_at).getTime();
        comparison = dateA - dateB;
      } else if (sortField === 'recipients') {
        const recipientsA = (a as any).recipients?.length || 0;
        const recipientsB = (b as any).recipients?.length || 0;
        comparison = recipientsA - recipientsB;
      }
      
      return sortOrder === 'desc' ? -comparison : comparison;
    });
  }, [filteredFiles, sortField, sortOrder]);

  // Handle sort column click
  const handleSort = (field: 'name' | 'datetime' | 'recipients') => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  // Frontend pagination - paginate the sorted files
  const totalPages = Math.ceil(sortedFiles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedFiles = sortedFiles.slice(startIndex, endIndex);
  const totalItems = sortedFiles.length;

  // Filter handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Reset to page 1 when sorting changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortField, sortOrder]);

  // Selection management functions
  const handleSelectItem = (itemId: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Helper function to validate email address
  const isValidEmail = (email: string): boolean => {
    // More strict email validation that rejects Turkish characters and other invalid characters
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    // Check for Turkish characters and other invalid characters
    const turkishChars = /[çğıöşüÇĞIİÖŞÜ]/;
    if (turkishChars.test(email)) {
      return false;
    }

    // Check for punycode domains (xn-- prefix)
    if (email.includes('xn--')) {
      return false;
    }

    // Check for other invalid characters
    const invalidChars = /[^a-zA-Z0-9._%+-@]/;
    if (invalidChars.test(email)) {
      return false;
    }

    return emailRegex.test(email);
  };




  // Handle file upload for CSV
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setCsvData(content);
      };
      reader.readAsText(file);
    }
  };

  // Handle policy change for selected files
  const handlePolicyChange = async () => {
    if (selectedItems.size === 0) {
      return { success: false, error: 'No files selected' };
    }

    // Check if we have policies selected for the asset types we have
    const hasFiles = selectedAssetsByType.files.length > 0;
    const hasFolders = selectedAssetsByType.folders.length > 0;

    if (hasFiles && !selectedPolicyId) {
      return { success: false, error: 'Please select a policy for files' };
    }

    if (hasFolders && !selectedFolderPolicyId) {
      return { success: false, error: 'Please select a policy for folders' };
    }

    setIsChangingPolicy(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Process file assets
      if (hasFiles && selectedPolicyId) {
        const filePolicy = filePolicies.find((p: any) => p.id === selectedPolicyId);
        if (!filePolicy) {
          throw new Error('File policy not found');
        }

        // Prepare bulk asset permissions creation
        const fileRequests = selectedAssetsByType.files.map(file => ({
          id: file.id,
          policy_id: selectedPolicyId
        }));

        try {
          console.log('Creating bulk asset permissions for files:', fileRequests);
          const bulkResponse = await apiService.createBulkAssetPermissions(fileRequests);
          console.log('Bulk asset permissions created:', bulkResponse);

          // Update files with new asset permissions IDs
          const fileUpdates = bulkResponse.results
            .filter((result: any) => !result.error)
            .map((result: any) => ({
              id: result.file_id,
              fields: {
                asset_permissions_id: result.asset_permissions_id
              }
            }));

          if (fileUpdates.length > 0) {
            console.log('Updating files with new asset permissions IDs:', fileUpdates);
            await apiService.batchUpdateFiles(fileUpdates);
          }

          successCount += bulkResponse.success_count;
          errorCount += bulkResponse.error_count;

          if (overwriteRecipients) {
            console.log('Overwriting recipients with file policy recipients');
            // TODO: Implement recipient overwrite logic
          }
        } catch (error: any) {
          console.error('Failed to create bulk asset permissions:', error);
          errorCount += selectedAssetsByType.files.length;
        }
      }

      // Process folder assets
      if (hasFolders && selectedFolderPolicyId) {
        const folderPolicy = folderPolicies.find((p: any) => p.id === selectedFolderPolicyId);
        if (!folderPolicy) {
          throw new Error('Folder policy not found');
        }

        // Prepare bulk asset permissions creation for folders
        const folderRequests = selectedAssetsByType.folders.map(folder => ({
          id: folder.id,
          policy_id: selectedFolderPolicyId
        }));

        try {
          console.log('Creating bulk asset permissions for folders:', folderRequests);
          const bulkResponse = await apiService.createBulkAssetPermissions(folderRequests);
          console.log('Bulk asset permissions created:', bulkResponse);

          // Update folders with new asset permissions IDs
          const folderUpdates = bulkResponse.results
            .filter((result: any) => !result.error)
            .map((result: any) => ({
              id: result.file_id,
              fields: {
                asset_permissions_id: result.asset_permissions_id
              }
            }));

          if (folderUpdates.length > 0) {
            console.log('Updating folders with new asset permissions IDs:', folderUpdates);
            await apiService.batchUpdateFiles(folderUpdates);
          }

          successCount += bulkResponse.success_count;
          errorCount += bulkResponse.error_count;

          if (overwriteRecipients) {
            console.log('Overwriting recipients with folder policy recipients');
            // TODO: Implement recipient overwrite logic
          }
        } catch (error: any) {
          console.error('Failed to create bulk asset permissions for folders:', error);
          errorCount += selectedAssetsByType.folders.length;
        }
      }

      return { success: true, successCount, errorCount };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to change policy'
      };
    } finally {
      setIsChangingPolicy(false);
    }
  };

  // Handle bulk blocking files
  const handleBulkBlockFiles = async () => {
    if (selectedItems.size === 0) {
      toast.error('No files selected', { duration: 3000 });
      return;
    }

    const success = await accessBlockingChallenge.executeWithChallenge(async () => {
      setIsBlockingFiles(true);
      try {
        const response = await apiService.bulkUpdateFileStatus(Array.from(selectedItems), 'blocked');

        if (response.success_count > 0) {
          toast.success(
            `Successfully blocked ${response.success_count} file${response.success_count > 1 ? 's' : ''}`,
            { duration: 4000 }
          );
        }

        if (response.error_count > 0) {
          toast.error(
            `Failed to block ${response.error_count} file${response.error_count > 1 ? 's' : ''}`,
            { duration: 4000 }
          );
        }

        // Refresh data
        queryClient.invalidateQueries({ queryKey: ['files'] });
        setSelectedItems(new Set());
        setShowBlockConfirm(false);
      } catch (error: any) {
        console.error('Error blocking files:', error);
        toast.error(error.response?.data?.error || 'Failed to block files', { duration: 4000 });
        throw error;
      } finally {
        setIsBlockingFiles(false);
      }
    });

    if (!success) {
      // Passkey challenge failed or was cancelled
      return;
    }
  };

  // Handle bulk unblocking files
  const handleBulkUnblockFiles = async () => {
    if (selectedItems.size === 0) {
      toast.error('No files selected', { duration: 3000 });
      return;
    }

    const success = await accessBlockingChallenge.executeWithChallenge(async () => {
      setIsBlockingFiles(true);
      try {
        const response = await apiService.bulkUpdateFileStatus(Array.from(selectedItems), 'ready');

        if (response.success_count > 0) {
          toast.success(
            `Successfully unblocked ${response.success_count} file${response.success_count > 1 ? 's' : ''}`,
            { duration: 4000 }
          );
        }

        if (response.error_count > 0) {
          toast.error(
            `Failed to unblock ${response.error_count} file${response.error_count > 1 ? 's' : ''}`,
            { duration: 4000 }
          );
        }

        // Refresh data
        queryClient.invalidateQueries({ queryKey: ['files'] });
        setSelectedItems(new Set());
        setShowUnblockConfirm(false);
      } catch (error: any) {
        console.error('Error unblocking files:', error);
        toast.error(error.response?.data?.error || 'Failed to unblock files', { duration: 4000 });
        throw error;
      } finally {
        setIsBlockingFiles(false);
      }
    });

    if (!success) {
      // Passkey challenge failed or was cancelled
      return;
    }
  };

  // Handle bulk deleting files
  const handleBulkDeleteFiles = async () => {
    if (selectedItems.size === 0) {
      toast.error('No files selected', { duration: 3000 });
      return;
    }

    setIsDeletingFiles(true);
    try {
      const response = await apiService.bulkDeleteFiles(Array.from(selectedItems));

      if (response.success_count > 0) {
        toast.success(
          `Successfully deleted ${response.success_count} file${response.success_count > 1 ? 's' : ''}. Storage cleanup in progress.`,
          { duration: 5000 }
        );
      }

      if (response.error_count > 0) {
        toast.error(
          `Failed to delete ${response.error_count} file${response.error_count > 1 ? 's' : ''}`,
          { duration: 4000 }
        );
      }

      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['files'] });
      setSelectedItems(new Set());
      setShowDeleteConfirm(false);
    } catch (error: any) {
      console.error('Error deleting files:', error);
      toast.error(error.response?.data?.error || 'Failed to delete files', { duration: 4000 });
    } finally {
      setIsDeletingFiles(false);
    }
  };

  // Export file key(s): single .txt or bulk .csv. Uses raw mode (KMS-decrypted key). Admin can export for company users via user_id.
  const handleExportKeys = async (fileIdOrIds?: string | string[]) => {
    const ids = fileIdOrIds
      ? (Array.isArray(fileIdOrIds) ? fileIdOrIds : [fileIdOrIds])
      : Array.from(selectedItems);
    if (ids.length === 0) {
      toast.error('No files selected');
      return;
    }
    const firstFile = filteredFiles.find((f: any) => f.id === ids[0]);
    const fileUserId = firstFile && 'user_id' in firstFile ? firstFile.user_id : undefined;
    const fileOwnerId = firstFile && 'owner_id' in firstFile ? firstFile.owner_id : undefined;
    const userIdRaw =
      currentUser?.role === 'admin' &&
      profile &&
      firstFile &&
      (fileUserId !== profile.id || fileOwnerId !== profile.id)
        ? (fileOwnerId || fileUserId)
        : undefined;
    const userId: string | undefined = typeof userIdRaw === 'string' ? userIdRaw : undefined;

    setIsExportingKeys(true);
    try {
      if (ids.length === 1) {
        const { blob, filename } = await apiService.exportFileKey('raw', ids[0], userId);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('File key exported');
      } else {
        const { blob, filename } = await apiService.exportFileKeysBulk('raw', ids, userId);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`${ids.length} file keys exported (CSV)`);
      }
    } catch (error: any) {
      const msg = error.response?.data?.error || error.message || 'Export failed';
      toast.error(msg);
    } finally {
      setIsExportingKeys(false);
    }
  };

  // Quick toggle for single file block/unblock
  const handleQuickToggleBlock = async (fileId: string, currentStatus: string, fileName: string) => {
    // Show confirmation modal
    setSingleToggleFile({ id: fileId, status: currentStatus, name: fileName });
    setShowSingleToggleConfirm(true);
  };

  const confirmSingleToggleBlock = async () => {
    if (!singleToggleFile) return;

    const success = await accessBlockingChallenge.executeWithChallenge(async () => {
      try {
        const newStatus = singleToggleFile.status === 'blocked' ? 'ready' : 'blocked';
        await apiService.updateFileStatus(singleToggleFile.id, newStatus);

        toast.success(
          newStatus === 'blocked' ? 'File blocked successfully' : 'File unblocked successfully',
          { duration: 3000 }
        );

        // Close modal and reset
        setShowSingleToggleConfirm(false);
        setSingleToggleFile(null);

        // Refresh data
        queryClient.invalidateQueries({ queryKey: ['files'] });
      } catch (error: any) {
        console.error('Error toggling file status:', error);
        toast.error(error.response?.data?.error || 'Failed to update file status', { duration: 4000 });
        throw error;
      }
    });

    if (!success) {
      // Passkey challenge failed or was cancelled
      return;
    }
  };

  // Check if any selected files are blocked (to show Unblock button)
  const hasBlockedFiles = useMemo(() => {
    // Only check status for "My assets" tab (not shared files)
    if (selectedTab !== 'my-assets' || selectedItems.size === 0) return false;

    return Array.from(selectedItems).some(itemId => {
      const file = (filteredFiles as any[]).find((f: any) => f.id === itemId);
      return file && 'status' in file && file.status === 'blocked';
    });
  }, [selectedItems, filteredFiles, selectedTab]);

  // Check if any selected files are ready/active (to show Block button)
  const hasUnblockedFiles = useMemo(() => {
    // Only check status for "My assets" tab (not shared files)
    if (selectedTab !== 'my-assets' || selectedItems.size === 0) return false;

    return Array.from(selectedItems).some(itemId => {
      const file = (filteredFiles as any[]).find((f: any) => f.id === itemId);
      return file && 'status' in file && file.status === 'ready';
    });
  }, [selectedItems, filteredFiles, selectedTab]);

  // Only show error if the current tab's data failed to load
  const currentTabError = selectedTab === 'shared-with-me' ? sharedFilesError : error;
  const currentTabLoading = selectedTab === 'shared-with-me' ? sharedFilesLoading : isLoading;

  // Categorize selected assets by type
  const selectedAssetsByType = useMemo(() => {
    if (!selectedItems.size || !filteredFiles.length) {
      return { files: [], folders: [] };
    }

    const files: any[] = [];
    const folders: any[] = [];

    filteredFiles.forEach((file: any) => {
      if (selectedItems.has(file.id)) {
        if (file.asset_type === 'file') {
          files.push(file);
        } else if (file.asset_type === 'folder') {
          folders.push(file);
        }
      }
    });

    return { files, folders };
  }, [selectedItems, filteredFiles]);

  // Filter policies by asset type
  const filePolicies = useMemo(() => {
    return policiesData?.filter((policy: any) => policy.asset_type === 'file') || [];
  }, [policiesData]);

  const folderPolicies = useMemo(() => {
    return policiesData?.filter((policy: any) => policy.asset_type === 'folder') || [];
  }, [policiesData]);

  if (currentTabError) {
    return (
      <div className="text-center py-12">
        <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading files</h3>
        <p className="mt-1 text-sm text-gray-500">Please try again later.</p>
      </div>
    );
  }

  // Remove global loading spinner - we'll show skeleton in the table area instead

  return (
    <div className="space-y-6">

      {/* Tabs */}
      <div className="mb-4 px-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
          {(() => {
            // Reader mode: only Shared with me and Requests (no My assets)
            if (currentUser?.mode === 'reader') {
              return [
                { key: 'shared-with-me', label: t('tabs.sharedWithMe') },
                { key: 'requests', label: t('tabs.requests') },
              ];
            }
            // Admin/sysadmin: only Company assets or All assets (no Shared with me, no Requests)
            if (effectiveMode === 'admin' || effectiveMode === 'sysadmin') {
              const label = effectiveMode === 'sysadmin' ? t('tabs.allAssets') : t('tabs.companyAssets');
              return [{ key: 'my-assets', label }];
            }
            // Standard user: My assets, Shared with me, Requests
            return [
              { key: 'my-assets', label: t('tabs.myAssets') },
              { key: 'shared-with-me', label: t('tabs.sharedWithMe') },
              { key: 'requests', label: t('tabs.requests') },
            ];
          })().map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setSelectedTab(tab.key as any);
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set('tab', tab.key);
                  return next;
                }, { replace: true });
                if (tab.key !== 'requests') {
                  setRequestsSubTab('received'); // Reset to received when switching away
                }
              }}
              className={`relative py-2 px-1 border-b-2 font-medium text-sm transition-colors ${selectedTab === tab.key
                ? 'border-governate-500 text-governate-600 dark:text-governate-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
            >
              {tab.label}
              {tab.key === 'requests' && hasPendingRequests && (
                <div className="absolute -top-0.5 -right-0.5 bg-orange-500 rounded-full h-2.5 w-2.5 border-2 border-white dark:border-gray-900"></div>
              )}
            </button>
          ))}

          {/* Sub-tabs that appear when Requests is selected */}
          {selectedTab === 'requests' && (
            <>
              <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-2"></div>
              {[
                { key: 'received', label: 'Received' },
                { key: 'sent', label: 'Sent' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setRequestsSubTab(tab.key as any)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${requestsSubTab === tab.key
                    ? 'border-governate-500 text-governate-600 dark:text-governate-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </>
          )}
        </div>
          
          {/* Right side: View Toggle, Asset Type Filter and Filter Button */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            {/* View Toggle - List/Grid */}
            <div className="flex items-center gap-1 border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 transition-colors ${
                  viewMode === 'list'
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
                title="List view"
              >
                {viewMode === 'list' ? (
                  <Bars3IconSolid className="w-4 h-4" />
                ) : (
                  <Bars3Icon className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
                title="Grid view"
              >
                {viewMode === 'grid' ? (
                  <Squares2X2IconSolid className="w-4 h-4" />
                ) : (
                  <Squares2X2Icon className="w-4 h-4" />
                )}
              </button>
            </div>
            
            {/* Asset Type Filter - Only show on My assets tab */}
            {selectedTab === 'my-assets' && (
              <div className="flex items-center gap-2">
                <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Type:</span>
                <div className="flex gap-1 border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
                  <button
                    onClick={() => setAssetType(undefined)}
                    className={`px-2 sm:px-3 py-1.5 text-xs font-medium transition-colors ${
                      assetType === undefined
                        ? 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300'
                        : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setAssetType('file')}
                    className={`px-2 sm:px-3 py-1.5 text-xs font-medium transition-colors ${
                      assetType === 'file'
                        ? 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300'
                        : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    Files
                  </button>
                  <button
                    onClick={() => setAssetType('folder')}
                    className={`px-2 sm:px-3 py-1.5 text-xs font-medium transition-colors ${
                      assetType === 'folder'
                        ? 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300'
                        : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    Folders
                  </button>
                </div>
              </div>
            )}
            
            {/* Filter Button */}
            <button
              onClick={() => setIsSearchModalOpen(true)}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
              title="Open search filters"
            >
              <FunnelIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Filters</span>
            </button>
          </div>
        </div>
      </div>

      {/* Selected Items Info and Bulk Actions */}
      {selectedItems.size > 0 && selectedTab !== 'requests' && selectedTab !== 'shared-with-me' && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4 mx-6">
          <div className="flex items-center justify-between">

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowAddRecipients(true)}
                disabled={isAddingRecipient}
                className="p-2 border-2 border-black dark:border-white text-black dark:text-white bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center rounded-lg"
                title="Add Recipients"
              >
                <div className="flex items-center gap-1">
                <PlusIcon className="w-4 h-4" />
                  <UserGroupIcon className="w-4 h-4" />
                </div>
              </button>
              <button
                onClick={() => setShowRemoveRecipients(true)}
                className="p-2 border-2 border-black dark:border-white text-black dark:text-white bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center rounded-lg"
                title="Remove Recipients"
              >
                <div className="flex items-center gap-1">
                  <MinusIcon className="w-4 h-4" />
                  <UserGroupIcon className="w-4 h-4" />
                </div>
              </button>
              {currentUser?.ownership_transfer_enabled !== false && (
              <button
                onClick={() => setShowChangeOwner(true)}
                disabled={isChangingOwner}
                className="p-2 border-2 border-black dark:border-white text-black dark:text-white bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center rounded-lg"
                title="Change Owner"
              >
                <div className="flex items-center gap-1">
                  <ArrowsRightLeftIcon className="w-4 h-4" />
                <UserIcon className="w-4 h-4" />
                </div>
              </button>
              )}
              <button
                onClick={() => setShowChangePolicy(true)}
                disabled={isChangingPolicy}
                className="p-2 border-2 border-black dark:border-white text-black dark:text-white bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center rounded-lg"
                title="Change Policy"
              >
                <ShieldCheckIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setShowManageTags(true)}
                className="p-2 border-2 border-black dark:border-white text-black dark:text-white bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center rounded-lg"
                title="Manage Tags"
              >
                <TagIcon className="w-4 h-4" />
              </button>
              {((selectedItems.size === 1 && currentUser?.allow_file_key_download) || (selectedItems.size > 1 && currentUser?.allow_multiple_file_key_download)) && (
              <button
                onClick={() => handleExportKeys()}
                disabled={isExportingKeys}
                className="p-2 border-2 border-black dark:border-white text-black dark:text-white bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center rounded-lg"
                title={selectedItems.size === 1 ? 'Export file key (.txt)' : 'Export file keys (.csv)'}
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
              </button>
              )}
              {hasUnblockedFiles && (
                <button
                  onClick={() => setShowBlockConfirm(true)}
                  disabled={isBlockingFiles}
                  className="p-2 border-2 border-yellow-600 text-yellow-600 bg-white hover:bg-yellow-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center rounded-lg"
                  title="Block"
                >
                  <LockClosedIcon className="w-4 h-4" />
                </button>
              )}
              {hasBlockedFiles && (
                <button
                  onClick={() => setShowUnblockConfirm(true)}
                  disabled={isBlockingFiles}
                  className="p-2 border-2 border-green-600 text-green-600 bg-white hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center rounded-lg"
                  title="Unblock"
                >
                  <LockOpenIcon className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isDeletingFiles}
                className="p-2 border-2 border-red-600 text-red-600 bg-white hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center rounded-lg"
                title="Delete"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Requests Tab Content */}
      {selectedTab === 'requests' && (
        <div>
          {/* Requests List */}
          <div>
            {(requestsSubTab === 'received' ? receivedRequestsLoading : sentRequestsLoading) ? (
              <div className="animate-pulse">
                <div className="px-6 py-4">
                  <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="flex space-x-4">
                        <div className="flex-1 space-y-2 py-1">
                          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (() => {
              let requests = requestsSubTab === 'received'
                ? (receivedRequestsData?.requests || [])
                : (sentRequestsData?.requests || []);

              // Apply filters if requests tab is selected and filters exist
              const shouldApplyFilters = searchSelectedTabs.has('requests');
              const hasFilters = searchTerm || emailSearch || dateFrom || dateTo;
              
              if (hasFilters && shouldApplyFilters) {
                requests = requests.filter((request: any) => {
                  // Email filter
                  let matchesEmail = true;
                  if (emailSearch && emailSearch.trim() !== '') {
                    const searchEmail = emailSearch.toLowerCase().trim();
                    if (requestsSubTab === 'received') {
                      // For received: filter by requester email
                      matchesEmail = request.requester_email?.toLowerCase().includes(searchEmail) || false;
                    } else {
                      // For sent: filter by owner email
                      matchesEmail = request.owner_email?.toLowerCase().includes(searchEmail) || false;
                    }
                  }

                  // Asset name filter
                  let matchesAssetName = true;
                  if (searchTerm && searchTerm.trim() !== '') {
                    matchesAssetName = request.file_name?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
                  }

                  // Date range filter
                  const requestDate = new Date(request.created_at);
                  const matchesDateFrom = dateFrom ? requestDate >= new Date(dateFrom) : true;
                  const matchesDateTo = dateTo ? requestDate <= new Date(dateTo) : true;

                  return matchesEmail && matchesAssetName && matchesDateFrom && matchesDateTo;
                });
              } else if (hasFilters && !shouldApplyFilters) {
                // If filters exist but requests tab is not selected, show empty
                requests = [];
              }

              // Sort requests: pending first, then by created_at desc
              const sortedRequests = [...requests].sort((a: any, b: any) => {
                if (a.status === 'pending' && b.status !== 'pending') return -1;
                if (a.status !== 'pending' && b.status === 'pending') return 1;
                return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
              });

              if (sortedRequests.length === 0) {
                return (
                  <div className="text-center py-12">
                    <PaperAirplaneIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No requests found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {requestsSubTab === 'received'
                        ? 'No access requests at this time.'
                        : 'You have not created any access requests.'}
                    </p>
                  </div>
                );
              }

              return (
                <div className={viewMode === 'grid' 
                  ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
                  : 'space-y-0'
                }>
                        {sortedRequests.map((request: any) => (
                    <div
                      key={request.id}
                      className={viewMode === 'grid'
                        ? 'bg-white dark:bg-black border border-gray-200 dark:border-gray-800 rounded-lg p-4 hover:shadow-md transition-shadow relative'
                        : 'bg-white dark:bg-black border-b border-gray-200 dark:border-gray-800 px-4 lg:px-6 py-3 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors relative'
                      }
                    >
                      {viewMode === 'grid' ? (
                        <>
                          {/* GRID: Header */}
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="flex-shrink-0">{getFileIcon(request.file_name || 'file', request.asset_type, request.metadata?.mime_type)}</div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {formatFileName(request.file_name || 'Unknown File', request.asset_type, request.metadata?.mime_type)}
                                </div>
                              </div>
                            </div>
                            {/* Three dots menu */}
                            <div className="relative flex-shrink-0 dropdown-menu-container">
                              <button
                                onClick={(e) => { e.stopPropagation(); setOpenDropdownId(openDropdownId === request.id ? null : request.id); }}
                                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                title="Actions"
                              >
                                <EllipsisVerticalIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                              </button>
                              {openDropdownId === request.id && (
                                <>
                                  <div className="fixed inset-0 z-10" onClick={() => setOpenDropdownId(null)}></div>
                                  <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20 dropdown-menu-container">
                                    <div className="py-1">
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleViewDetails(request); setOpenDropdownId(null); }}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                      >
                                        <EyeIcon className="w-4 h-4" />
                                        View Details
                                      </button>
                                      {requestsSubTab === 'received' && request.status === 'pending' && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleQuickRejectClick(request); setOpenDropdownId(null); }}
                                          disabled={isResponding || isRejecting}
                                          className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          <XMarkIcon className="w-4 h-4" />
                                          Reject Request
                                        </button>
                                      )}
                                      {requestsSubTab === 'sent' && request.status === 'pending' && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleCancelClick(request.id); setOpenDropdownId(null); }}
                                          className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                                        >
                                          <XMarkIcon className="w-4 h-4" />
                                          Cancel Request
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                          {/* GRID: Details */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 text-sm flex-1 min-w-0">
                                {requestsSubTab === 'received' ? (
                                  <>
                                    <UserIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    <span className="text-gray-600 dark:text-gray-400 truncate">{request.requester_email || 'Unknown'}</span>
                                  </>
                                ) : (
                                  <span className="text-gray-600 dark:text-gray-400">Status:</span>
                                )}
                              </div>
                              {requestsSubTab === 'sent' && <div className="flex-shrink-0">{getStatusBadge(request.status)}</div>}
                            </div>
                            {requestsSubTab === 'received' && (
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Status:</span>
                                {getStatusBadge(request.status)}
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              {request.request_type === 'access_period_limit'
                                ? <ClockIcon className="w-4 h-4 text-gray-400" />
                                : <HashtagIcon className="w-4 h-4 text-gray-400" />}
                              <span className="text-sm text-gray-900 dark:text-gray-100">
                                {getRequestTypeLabel(request.request_type)}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {formatDate(request.created_at)}
                            </div>
                          </div>
                        </>
                      ) : (
                        /* LIST MODE: single compact row */
                        <div className="flex items-center gap-3 lg:gap-4">
                          <div className="flex-shrink-0 text-gray-400">
                            {request.request_type === 'access_period_limit'
                              ? <ClockIcon className="w-4 h-4" />
                              : <HashtagIcon className="w-4 h-4" />}
                          </div>
                          <div className="flex-shrink-0">{getFileIcon(request.file_name || 'file', request.asset_type, request.metadata?.mime_type)}</div>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate block">
                              {formatFileName(request.file_name || 'Unknown File', request.asset_type, request.metadata?.mime_type)}
                            </span>
                          </div>
                          {requestsSubTab === 'received' && (
                            <div className="hidden lg:flex items-center gap-1.5 w-52 shrink-0 min-w-0">
                              <UserIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                              <span className="text-sm text-gray-500 dark:text-gray-400 truncate">{request.requester_email || 'Unknown'}</span>
                            </div>
                          )}
                          <div className="flex-shrink-0">{getStatusBadge(request.status)}</div>
                          <div className="hidden lg:block text-xs text-gray-500 dark:text-gray-400 shrink-0 w-36 text-right">
                            {formatDate(request.created_at)}
                          </div>
                          {/* Three dots menu */}
                          <div className="relative flex-shrink-0 dropdown-menu-container">
                            <button
                              onClick={(e) => { e.stopPropagation(); setOpenDropdownId(openDropdownId === request.id ? null : request.id); }}
                              className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                              title="Actions"
                            >
                              <EllipsisVerticalIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                            </button>
                            {openDropdownId === request.id && (
                              <>
                                <div className="fixed inset-0 z-10" onClick={() => setOpenDropdownId(null)}></div>
                                <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20 dropdown-menu-container">
                                  <div className="py-1">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleViewDetails(request); setOpenDropdownId(null); }}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                    >
                                      <EyeIcon className="w-4 h-4" />
                                      View Details
                                    </button>
                                    {requestsSubTab === 'received' && request.status === 'pending' && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleQuickRejectClick(request); setOpenDropdownId(null); }}
                                        disabled={isResponding || isRejecting}
                                        className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        <XMarkIcon className="w-4 h-4" />
                                        Reject Request
                                      </button>
                                    )}
                                    {requestsSubTab === 'sent' && request.status === 'pending' && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleCancelClick(request.id); setOpenDropdownId(null); }}
                                        className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                                      >
                                        <XMarkIcon className="w-4 h-4" />
                                        Cancel Request
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Files Table - Only show for my-assets and shared-with-me tabs */}
      {selectedTab !== 'requests' && (
        <div>
          {currentTabLoading ? (
            <div className="animate-pulse">
              <div className="px-6 py-4">
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex space-x-4">
                      <div className="flex-1 space-y-2 py-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      </div>
                      <div className="flex-1 space-y-2 py-1">
                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                      </div>
                      <div className="flex-1 space-y-2 py-1">
                        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      </div>
                      <div className="flex-1 space-y-2 py-1">
                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                      </div>
                      <div className="flex-1 space-y-2 py-1">
                        <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : paginatedFiles.length === 0 ? (
            <div className="text-center py-8">
              <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {selectedTab === 'shared-with-me' ? t('empty.noShared') : t('empty.title')}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm ? t('empty.searchHint') :
                  selectedTab === 'shared-with-me' ? t('empty.noSharedDescription') : t('empty.getStartedHint')}
              </p>
            </div>
          ) : (
            <div className="bg-white dark:bg-black">
              {/* Sorting Controls - Button Style */}
              <div className="bg-white dark:bg-black rounded-lg shadow mb-4 px-4 py-3 border-b border-gray-200 dark:border-gray-800">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center space-x-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('sort.sortBy')}</span>
                    <button
                      onClick={() => handleSort('name')}
                      className={`text-sm px-3 py-1.5 rounded-md transition-colors ${
                        sortField === 'name'
                          ? 'bg-governate-100 text-governate-700 dark:bg-governate-900/30 dark:text-governate-300 font-medium'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        <span>{t('sort.fileName')}</span>
                        {sortField === 'name' && (
                          <span className="text-governate-600 dark:text-governate-400">
                            {sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </button>
                    <button
                      onClick={() => handleSort('datetime')}
                      className={`text-sm px-3 py-1.5 rounded-md transition-colors ${
                        sortField === 'datetime'
                          ? 'bg-governate-100 text-governate-700 dark:bg-governate-900/30 dark:text-governate-300 font-medium'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        <span>{selectedTab === 'shared-with-me' ? t('sort.sharedAt') : t('sort.createdAt')}</span>
                        {sortField === 'datetime' && (
                          <span className="text-governate-600 dark:text-governate-400">
                            {sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </button>
                      {selectedTab !== 'shared-with-me' && (
                      <button
                        onClick={() => handleSort('recipients')}
                        className={`text-sm px-3 py-1.5 rounded-md transition-colors ${
                          sortField === 'recipients'
                            ? 'bg-governate-100 text-governate-700 dark:bg-governate-900/30 dark:text-governate-300 font-medium'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                      >
                        <div className="flex items-center gap-1">
                          <span>{t('sort.numberOfRecipients')}</span>
                          {sortField === 'recipients' && (
                            <span className="text-governate-600 dark:text-governate-400">
                              {sortOrder === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </button>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t(sortedFiles.length === 1 ? 'count.asset' : 'count.assets', { count: sortedFiles.length })}
                  </div>
                </div>
              </div>

              {/* Card View - List or Grid */}
              <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-0'}>
                    {paginatedFiles.map((file: any) => {
                      const canEdit = canPerformActions(file);
                  
                  // Get status circle color
                  const getStatusCircle = () => {
                    if (file.status === 'deleted') {
                      return <div className="w-3 h-3 rounded-full bg-gray-400"></div>;
                    } else if (file.status === 'blocked') {
                      return <div className="w-3 h-3 rounded-full bg-yellow-500"></div>;
                    } else {
                      return <div className="w-3 h-3 rounded-full bg-green-500"></div>;
                    }
                  };
                  
                      return (
                    <div
                      key={file.id}
                      onClick={() => {
                        if (canEdit && file.status !== 'deleted') {
                          navigate(`/assets/${file.id}/manage`, { state: { fileData: file } });
                        }
                      }}
                      className={viewMode === 'grid' 
                        ? `relative bg-white dark:bg-black border border-gray-200 dark:border-gray-800 rounded-lg p-4 hover:shadow-md transition-shadow ${
                        canEdit && file.status !== 'deleted' ? 'cursor-pointer' : ''
                          }`
                        : `relative bg-white dark:bg-black border-b border-gray-200 dark:border-gray-800 px-4 lg:px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors ${
                            canEdit && file.status !== 'deleted' ? 'cursor-pointer' : ''
                          }`
                      }
                    >
                      {/* Mobile: More Menu - Top Right */}
                      <div className="md:hidden absolute top-2 right-2 z-10" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenFileMenu(openFileMenu === file.id ? null : file.id);
                          }}
                          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                          title={t('table.moreOptions')}
                        >
                          <EllipsisVerticalIcon className="w-5 h-5" />
                        </button>
                        
                        {/* Dropdown Menu */}
                        {openFileMenu === file.id && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setOpenFileMenu(null)}
                            />
                            <div className="absolute right-0 mt-2 w-72 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg z-20 overflow-hidden">
                              {/* Metadata Section */}
                              <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900">
                                Details
                              </div>
                              <div className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300 space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-gray-500 dark:text-gray-400">{selectedTab === 'shared-with-me' ? 'Shared at' : 'Created at'}:</span>
                                  <span>{selectedTab === 'shared-with-me' ? formatDate(file.shared_at) : formatDate(file.created_at)}</span>
                                </div>
                                {selectedTab === 'shared-with-me' ? (
                                  <>
                                    <div className="flex justify-between">
                                      <span className="text-gray-500 dark:text-gray-400">Access:</span>
                                      <span>{file.times_accessible ? `${file.total_accesses}/${file.times_accessible}` : '-'}</span>
                                    </div>
                                    {file.allowed_access_from && file.allowed_access_to && (
                                      <div className="flex justify-between">
                                        <span className="text-gray-500 dark:text-gray-400">Period:</span>
                                        <span className="text-xs">{formatDate(file.allowed_access_from)} - {formatDate(file.allowed_access_to)}</span>
                                      </div>
                                    )}
                                    {file.owner_email && (
                                      <div className="flex justify-between">
                                        <span className="text-gray-500 dark:text-gray-400">Owner:</span>
                                        <span className="truncate">{file.owner_email}</span>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="flex justify-between">
                                    <span className="text-gray-500 dark:text-gray-400">{t('table.recipients')}</span>
                                    <span>
                                      {(() => {
                                        if (!profile) return 'Unknown';
                                        if (permissions.canViewAllLogs) {
                                          return file.recipients.length === 0
                                            ? t('table.noRecipients')
                                            : t(file.recipients.length === 1 ? 'count.recipient' : 'count.recipients', { count: file.recipients.length });
                                        }
                                        const isOnlyCurrentUser = file.recipients.length === 1 && file.recipients[0].user_id === profile.id;
                                        if (isOnlyCurrentUser) return t('table.onlyYou');
                                        const otherRecipients = file.recipients.filter((recipient: any) => recipient.user_id !== profile.id);
                                        return otherRecipients.length === 0 ? t('table.onlyYou') : t(otherRecipients.length === 1 ? 'count.recipient' : 'count.recipients', { count: otherRecipients.length });
                                      })()}
                                    </span>
                                  </div>
                                )}
                              </div>
                              
                              {/* Actions Section */}
                              <div className="border-t border-gray-200 dark:border-gray-700">
<div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900">
                                    {t('table.actions')}
                                </div>
                                <div className="py-1">
                                  {selectedTab === 'shared-with-me' ? (
                                    <>
                                      {!canEdit && (
                                        <>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setOpenFileMenu(null);
                                              setSelectedFileForRequest({ id: file.id, name: file.file_name });
                                              setShowRequestAccessModal(true);
                                              setRequestType('access_count_limit');
                                              setRequestReason('');
                                            }}
                                            className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                          >
                                            <PaperAirplaneIcon className="w-4 h-4" />
                                            {t('table.requestAccessLimitChange')}
                                          </button>
                                          {file.allowed_permissions?.share && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenFileMenu(null);
                                                setSelectedFileForShare({ id: file.id, name: file.file_name });
                                                setShareRecipientEmail('');
                                                setShowShareModal(true);
                                              }}
                                              className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                            >
                                              <UserGroupIcon className="w-4 h-4" />
                                              {t('table.shareAsset')}
                                            </button>
                                          )}
                                        </>
                                      )}
                                      {canEdit && (
                                        <>
                                          <button
                                            onClick={() => {
                                              setOpenFileMenu(null);
                                              navigate(`/assets/${file.id}/manage`, { state: { fileData: file } });
                                            }}
                                            className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                          >
                                            <PencilIcon className="w-4 h-4" />
                                            {t('table.editAsset')}
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setOpenFileMenu(null);
                                              navigate(`/logs?resource_id=${file.id}`);
                                            }}
                                            className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                          >
                                            <ClockIcon className="w-4 h-4" />
                                            {t('table.viewActivityHistory')}
                                          </button>
                                        </>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      {file.status === 'deleted' ? (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setOpenFileMenu(null);
                                            navigate(`/logs?resource_id=${file.id}`);
                                          }}
                                          className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                        >
                                          <ClockIcon className="w-4 h-4" />
                                          {t('table.viewActivityHistory')}
                                        </button>
                                      ) : (
                                        <>
                                          <button
                                            onClick={() => {
                                              setOpenFileMenu(null);
                                              navigate(`/assets/${file.id}/manage`, { state: { fileData: file } });
                                            }}
                                            className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                          >
                                            <PencilIcon className="w-4 h-4" />
                                            {t('table.editAsset')}
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setOpenFileMenu(null);
                                              navigate(`/logs?resource_id=${file.id}`);
                                            }}
                                            className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                          >
                                            <ClockIcon className="w-4 h-4" />
                                            {t('table.viewActivityHistory')}
                                          </button>
                                          {canPerformActions(file) && currentUser?.allow_file_key_download && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenFileMenu(null);
                                                handleExportKeys(file.id);
                                              }}
                                              disabled={isExportingKeys}
                                              className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50"
                                            >
                                              <KeyIcon className="w-4 h-4" />
                                              Export file key
                                            </button>
                                          )}
                                          {(file.status === 'blocked' || file.status === 'ready') && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenFileMenu(null);
                                                handleQuickToggleBlock(file.id, file.status, file.file_name);
                                              }}
                                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                              style={{ color: file.status === 'blocked' ? '#16a34a' : '#ca8a04' }}
                                            >
                                              {file.status === 'blocked' ? (
                                                <LockOpenIcon className="w-4 h-4" />
                                              ) : (
                                                <LockClosedIcon className="w-4 h-4" />
                                              )}
                                              {file.status === 'blocked' ? 'Unblock access' : 'Block access'}
                                            </button>
                                          )}
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setOpenFileMenu(null);
                                              setSelectedItems(new Set([file.id]));
                                              setShowDeleteConfirm(true);
                                            }}
                                            className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                                          >
                                            <TrashIcon className="w-4 h-4" />
                                            Delete asset
                                          </button>
                                        </>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      <div className={viewMode === 'grid' 
                        ? 'flex flex-col gap-3'
                        : 'flex flex-col lg:flex-row lg:items-center gap-4'
                      }>
                        {/* Left Section: Checkbox, Icon, File Info */}
                        <div className={viewMode === 'grid' 
                          ? 'flex items-start gap-3 flex-1 min-w-0'
                          : 'flex items-start gap-2 md:gap-3 lg:gap-4 flex-1 min-w-0'
                        }>
                        {selectedTab !== 'shared-with-me' && (
                          <input
                            type="checkbox"
                            checked={selectedItems.has(file.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleSelectItem(file.id);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="mt-1 h-4 w-4 text-governate-600 focus:ring-governate-500 border-gray-300 rounded flex-shrink-0"
                          />
                        )}
                          {selectedTab === 'my-assets' && <div className="flex-shrink-0 mt-1 hidden md:block">{getStatusCircle()}</div>}
                          <div className="flex-shrink-0 scale-75 md:scale-100 origin-left">{getFileIcon(file.file_name, file.asset_type, file.metadata?.mime_type || (file as any).mime_type)}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 lg:gap-3">
                              <div className="text-xs md:text-sm lg:text-base font-medium text-gray-900 truncate">{formatFileName(file.file_name, file.asset_type, file.metadata?.mime_type || (file as any).mime_type)}</div>
                              </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                                {(file.metadata?.file_size ?? (file as any).file_size) ?
                                  (() => {
                                    const size = file.metadata?.file_size ?? (file as any).file_size;
                                    return size < 1024
                                      ? `${size} B`
                                      : size < 1024 * 1024
                                        ? `${(size / 1024).toFixed(1)} KB`
                                        : `${(size / (1024 * 1024)).toFixed(1)} MB`;
                                  })()
                                  : 'Unknown size'}
                              </div>
                            {permissions.canViewAllLogs && selectedTab === 'my-assets' && file.owner_email && (
                              <div className="text-xs text-gray-400 mt-0.5 truncate">{file.owner_email}</div>
                            )}
                            </div>
                          </div>

                        {/* Middle Section: Metadata - Hidden on mobile */}
                        {viewMode === 'list' && (
                        <div className="hidden md:flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-6">
                          <div className="text-xs lg:text-sm text-gray-900 lg:w-40">
                          {selectedTab === 'shared-with-me' ? formatDate(file.shared_at) : formatDate(file.created_at)}
                              </div>
                          <div className="text-xs lg:text-sm text-gray-900 lg:w-32">
                            {selectedTab === 'shared-with-me' ? (
                              <div className="flex flex-col">
                                <span>{file.times_accessible ? `${file.total_accesses}/${file.times_accessible}` : '-'}</span>
                                {file.allowed_access_from && file.allowed_access_to && (
                                  <span className="text-xs text-gray-500">
                                    {formatDate(file.allowed_access_from)} - {formatDate(file.allowed_access_to)}
                                  </span>
                                )}
                                </div>
                            ) : (
                              (() => {
                                    if (!profile) return 'Unknown';
                                    if (permissions.canViewAllLogs) {
                                      return file.recipients.length === 0
                                        ? 'No recipients'
                                        : `${file.recipients.length} recipient${file.recipients.length > 1 ? 's' : ''}`;
                                    }
                                const isOnlyCurrentUser = file.recipients.length === 1 && file.recipients[0].user_id === profile.id;
                                if (isOnlyCurrentUser) return 'Only you';
                                const otherRecipients = file.recipients.filter((recipient: any) => recipient.user_id !== profile.id);
                                return otherRecipients.length === 0 ? 'Only you' : `${otherRecipients.length} recipient${otherRecipients.length > 1 ? 's' : ''}`;
                              })()
                            )}
                              </div>
                        </div>
                        )}

                        {/* Grid view metadata - Hidden on mobile */}
                        {viewMode === 'grid' && (
                          <div className="hidden md:block space-y-1 text-xs text-gray-600 dark:text-gray-400">
                            <div className="flex items-center gap-2">
                              <ClockIcon className="w-3 h-3" />
                              <span>{selectedTab === 'shared-with-me' ? formatDate(file.shared_at) : formatDate(file.created_at)}</span>
                            </div>
                            {selectedTab === 'shared-with-me' ? (
                              <>
                                <div className="flex items-center gap-2">
                                  <span>{file.times_accessible ? `${file.total_accesses}/${file.times_accessible}` : '-'}</span>
                                </div>
                                {file.owner_email && (
                                  <div className="flex items-center gap-2 truncate">
                                    <UserIcon className="w-3 h-3" />
                                    <span className="truncate">{file.owner_email}</span>
                                  </div>
                                )}
                              </>
                            ) : permissions.canViewAllLogs && file.owner_email ? (
                              <div className="flex items-center gap-2 truncate">
                                <UserIcon className="w-3 h-3" />
                                <span className="truncate">{file.owner_email}</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <UserGroupIcon className="w-3 h-3" />
                                <span>
                                  {(() => {
                                    if (!profile) return 'Unknown';
                                    if (permissions.canViewAllLogs) {
                                      return file.recipients.length === 0
                                        ? 'No recipients'
                                        : `${file.recipients.length} recipient${file.recipients.length > 1 ? 's' : ''}`;
                                    }
                                    const isOnlyCurrentUser = file.recipients.length === 1 && file.recipients[0].user_id === profile.id;
                                    if (isOnlyCurrentUser) return 'Only you';
                                    const otherRecipients = file.recipients.filter((recipient: any) => recipient.user_id !== profile.id);
                                    return otherRecipients.length === 0 ? 'Only you' : `${otherRecipients.length} recipient${otherRecipients.length > 1 ? 's' : ''}`;
                                  })()}
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Right Section: Actions - Hidden on mobile, shown on desktop */}
                        <div 
                          className={viewMode === 'grid'
                            ? 'hidden md:flex flex-wrap items-center gap-2 justify-end'
                            : 'hidden md:flex flex-wrap lg:flex-nowrap items-center gap-2 lg:gap-2 lg:w-32 lg:justify-end flex-shrink-0'
                          }
                          onClick={(e) => e.stopPropagation()}
                        >
                          {selectedTab === 'shared-with-me' ? (
                            <>
                              {!canEdit && (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedFileForRequest({ id: file.id, name: file.file_name });
                                      setShowRequestAccessModal(true);
                                      setRequestType('access_count_limit');
                                      setRequestReason('');
                                    }}
                                    className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                                    title="Request access limit change"
                                  >
                                    <PaperAirplaneIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                                  </button>
                                  {file.allowed_permissions?.share && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedFileForShare({ id: file.id, name: file.file_name });
                                        setShareRecipientEmail('');
                                        setShowShareModal(true);
                                      }}
                                      className="p-2 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                      title={t('table.shareAsset')}
                                    >
                                      <UserGroupIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                                    </button>
                                  )}
                                </>
                              )}
                              {canEdit && (
                                <>
                                  <button
                                    onClick={() => navigate(`/assets/${file.id}/manage`, { state: { fileData: file } })}
                                    className="p-2 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                    title={t('table.editAsset')}
                                  >
                                    <PencilIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/logs?resource_id=${file.id}`);
                                    }}
                                    className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                                    title={t('table.viewActivityHistory')}
                                  >
                                    <ClockIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                                  </button>
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              {file.status === 'deleted' ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/logs?resource_id=${file.id}`);
                                  }}
                                  className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                                  title={t('table.viewActivityHistory')}
                                >
                                  <ClockIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                                </button>
                              ) : (
                                <>
                                  <button
                                    onClick={() => navigate(`/assets/${file.id}/manage`, { state: { fileData: file } })}
                                    className="p-2 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                    title={t('table.editAsset')}
                                  >
                                    <PencilIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/logs?resource_id=${file.id}`);
                                    }}
                                    className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                                    title={t('table.viewActivityHistory')}
                                  >
                                    <ClockIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                                  </button>
                                  {canEdit && currentUser?.allow_file_key_download && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleExportKeys(file.id);
                                      }}
                                      disabled={isExportingKeys}
                                      className="p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                                      title="Export file key"
                                    >
                                      <KeyIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                                    </button>
                                  )}
                                  {(file.status === 'blocked' || file.status === 'ready') && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleQuickToggleBlock(file.id, file.status, file.file_name);
                                      }}
                                      className={`p-2 rounded-lg transition-colors ${file.status === 'blocked'
                                        ? 'bg-green-50 text-green-600 hover:bg-green-100'
                                        : 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100'
                                        }`}
                                      title={file.status === 'blocked' ? 'Unblock access' : 'Block access'}
                                    >
                                      {file.status === 'blocked' ? (
                                        <LockOpenIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                                      ) : (
                                        <LockClosedIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                                      )}
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedItems(new Set([file.id]));
                                      setShowDeleteConfirm(true);
                                    }}
                                    className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
                                    title="Delete asset"
                                  >
                                    <TrashIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                                  </button>
                                </>
                              )}
                            </>
                          )}
                        </div>

                      </div>
                    </div>
                      );
                    })}
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalItems > itemsPerPage && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium text-gray-900">{startIndex + 1}</span>
                    {' '}to{' '}
                    <span className="font-medium text-gray-900">
                      {Math.min(endIndex, totalItems)}
                    </span>
                    {' '}of{' '}
                    <span className="font-medium text-gray-900">{totalItems}</span>
                    {' '}results
                  </p>
                  <div className="flex items-center gap-2">
                    <label htmlFor="items-per-page" className="text-sm text-gray-700">
                      Show:
                    </label>
                    <select
                      id="items-per-page"
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="px-2 py-1 border border-gray-300 rounded-md text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                    </select>
                  </div>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}


      {/* Add Recipients Modal */}
      {
        showAddRecipients && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
            onClick={() => {
              setShowAddRecipients(false);
              setNewRecipient('');
              setCsvData('');
              setRecipientsToAdd([]);
            }}
          >
            <div
              className="bg-white rounded-lg p-6 w-full max-w-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Add Recipients</h3>
                <button
                  onClick={() => {
                    setShowAddRecipients(false);
                    setNewRecipient('');
                    setCsvData('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Add recipients to {selectedItems.size} selected file{selectedItems.size > 1 ? 's' : ''}.
                </p>
              </div>

              <div className="space-y-6">
                {/* Single Email Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Add Single Email
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="email"
                      value={newRecipient}
                      onChange={(e) => setNewRecipient(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newRecipient.trim() && isValidEmail(newRecipient.trim())) {
                          setRecipientsToAdd(prev => {
                            const email = newRecipient.trim();
                            if (!prev.includes(email)) {
                              return [...prev, email];
                            }
                            return prev;
                          });
                          setNewRecipient('');
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                      placeholder="Enter email address"
                    />
                    <button
                      onClick={() => {
                        if (newRecipient.trim() && isValidEmail(newRecipient.trim())) {
                          // Add to recipients list (avoid duplicates)
                          setRecipientsToAdd(prev => {
                            const email = newRecipient.trim();
                            if (!prev.includes(email)) {
                              return [...prev, email];
                            }
                            return prev;
                          });
                          setNewRecipient('');
                        }
                      }}
                      disabled={!newRecipient.trim() || !isValidEmail(newRecipient.trim())}
                      className="p-2 border-2 border-black text-black text-sm font-medium bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-lg"
                      title="Add recipient"
                    >
                      <PlusIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* CSV Import */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    Import from CSV
                    <div className="ml-2 relative group">
                      <div className="w-4 h-4 bg-gray-400 rounded-full flex items-center justify-center cursor-help">
                        <span className="text-white text-xs font-bold">i</span>
                      </div>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                        Enter comma-separated emails: john@example.com, jane@example.com
                      </div>
                    </div>
                  </label>

                  <div className="space-y-3">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileUpload}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-governate-50 file:text-governate-700 hover:file:bg-governate-100"
                    />

                    {csvData && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">CSV Preview</label>
                        <textarea
                          value={csvData}
                          onChange={(e) => setCsvData(e.target.value)}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent font-mono text-sm"
                          placeholder="email&#10;john@example.com&#10;jane@example.com"
                        />
                      </div>
                    )}

                    <button
                      onClick={() => {
                        if (csvData.trim()) {
                          // Parse comma-separated emails and add to recipients list
                          const emails = csvData.trim().split(/[,\n]/).map(email => email.trim()).filter(email => email && isValidEmail(email));
                          // Remove duplicates
                          const uniqueEmails = Array.from(new Set(emails));
                          setRecipientsToAdd(prev => {
                            const combined = [...prev, ...uniqueEmails];
                            return Array.from(new Set(combined)); // Remove duplicates from combined list
                          });
                          setCsvData('');
                        }
                      }}
                      disabled={!csvData.trim()}
                      className="w-full px-4 py-2 border-2 border-black text-black text-sm font-medium bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2 rounded-lg"
                    >
                      <DocumentArrowUpIcon className="w-4 h-4" />
                      <span>Import from CSV</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Recipients to Add List */}
              {recipientsToAdd.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Recipients to Add ({recipientsToAdd.length})
                  </h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-32 overflow-y-auto">
                    <div className="flex flex-wrap gap-2">
                      {recipientsToAdd.map((email, index) => (
                        <div key={index} className="flex items-center space-x-2 bg-white border border-gray-300 rounded-lg px-3 py-1">
                          <span className="text-sm text-gray-700">{email}</span>
                          <button
                            onClick={() => {
                              setRecipientsToAdd(prev => prev.filter((_, i) => i !== index));
                            }}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowAddRecipients(false);
                    setNewRecipient('');
                    setCsvData('');
                    setRecipientsToAdd([]);
                  }}
                  className="px-4 py-2 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (recipientsToAdd.length > 0) {
                      setIsAddingRecipient(true);
                      try {
                        // Use bulk endpoint to add all recipients at once
                        const response = await apiService.bulkAddRecipients(Array.from(selectedItems), recipientsToAdd.map(email => ({
                          type: 'email',
                          value: email,
                          // times_accessible now comes from AssetPermissions (backend auto-populates)
                          is_external: true,
                        })));

                        // Store the count of recipients we're adding for toast messages
                        const recipientCount = recipientsToAdd.length;

                        // Show success and close modal (invite emails are sent or skipped by backend based on settings)
                        if (response.success_count > 0) {
                          toast.success(
                            `${recipientCount} recipient${recipientCount > 1 ? 's' : ''} added successfully to ${selectedItems.size} file${selectedItems.size > 1 ? 's' : ''}!`,
                            {
                              duration: 4000,
                            }
                          );
                        }

                        if (response.error_count > 0) {
                          toast.error(
                            `Failed to add recipients to ${response.error_count} file${response.error_count > 1 ? 's' : ''}`,
                            {
                              duration: 4000,
                            }
                          );
                        }

                        setShowAddRecipients(false);
                        setNewRecipient('');
                        setCsvData('');
                        setRecipientsToAdd([]);

                        queryClient.invalidateQueries({ queryKey: ['files'] });
                        queryClient.invalidateQueries({ queryKey: ['shared-files'] });
                      } catch (error: any) {
                        console.error('Error adding recipients:', error);
                        toast.error(error.response?.data?.error || 'Failed to add recipients', {
                          duration: 4000,
                        });
                      } finally {
                        setIsAddingRecipient(false);
                      }
                    }
                  }}
                  disabled={recipientsToAdd.length === 0 || isAddingRecipient}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 rounded-lg"
                >
                  {isAddingRecipient ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <CheckIcon className="w-4 h-4" />
                      <span>Save</span>
                    </>
                  )}
                  {recipientsToAdd.length > 0 && (
                    <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                      {recipientsToAdd.length}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Remove Recipients Modal */}
      {
        showRemoveRecipients && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
            onClick={() => {
              setShowRemoveRecipients(false);
              setNewRecipient('');
              setCsvData('');
              setRecipientsToRemove([]);
            }}
          >
            <div
              className="bg-white rounded-lg p-6 w-full max-w-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Remove Recipients</h3>
                <button
                  onClick={() => {
                    setShowRemoveRecipients(false);
                    setNewRecipient('');
                    setCsvData('');
                    setRecipientsToRemove([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Remove recipients from {selectedItems.size} selected file{selectedItems.size > 1 ? 's' : ''}.
                </p>
              </div>

              <div className="space-y-6">
                {/* Single Email Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Remove Single Email
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="email"
                      value={newRecipient}
                      onChange={(e) => setNewRecipient(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newRecipient.trim() && isValidEmail(newRecipient.trim())) {
                          setRecipientsToRemove(prev => {
                            const email = newRecipient.trim();
                            if (!prev.includes(email)) {
                              return [...prev, email];
                            }
                            return prev;
                          });
                          setNewRecipient('');
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                      placeholder="Enter email address to remove"
                    />
                    <button
                      onClick={() => {
                        if (newRecipient.trim() && isValidEmail(newRecipient.trim())) {
                          setRecipientsToRemove(prev => {
                            const email = newRecipient.trim();
                            if (!prev.includes(email)) {
                              return [...prev, email];
                            }
                            return prev;
                          });
                          setNewRecipient('');
                        }
                      }}
                      disabled={!newRecipient.trim() || !isValidEmail(newRecipient.trim())}
                      className="p-2 border-2 border-black text-black text-sm font-medium bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-lg"
                      title="Add recipient to remove"
                    >
                      <PlusIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* CSV Import */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    Import from CSV
                    <div className="ml-2 relative group">
                      <div className="w-4 h-4 bg-gray-400 rounded-full flex items-center justify-center cursor-help">
                        <span className="text-white text-xs font-bold">i</span>
                      </div>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                        Enter comma-separated emails: john@example.com, jane@example.com
                      </div>
                    </div>
                  </label>

                  <div className="space-y-3">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileUpload}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-governate-50 file:text-governate-700 hover:file:bg-governate-100"
                    />

                    {csvData && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">CSV Preview</label>
                        <textarea
                          value={csvData}
                          onChange={(e) => setCsvData(e.target.value)}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent font-mono text-sm"
                          placeholder="email&#10;john@example.com&#10;jane@example.com"
                        />
                      </div>
                    )}

                    <button
                      onClick={() => {
                        if (csvData.trim()) {
                          const emails = csvData.trim().split(/[,\n]/).map(email => email.trim()).filter(email => email && isValidEmail(email));
                          const uniqueEmails = Array.from(new Set(emails));
                          setRecipientsToRemove(prev => {
                            const combined = [...prev, ...uniqueEmails];
                            return Array.from(new Set(combined));
                          });
                          setCsvData('');
                        }
                      }}
                      disabled={!csvData.trim()}
                      className="w-full px-4 py-2 border-2 border-black text-black text-sm font-medium bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2 rounded-lg"
                    >
                      <DocumentArrowUpIcon className="w-4 h-4" />
                      <span>Import from CSV</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Recipients to Remove List */}
              {recipientsToRemove.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Recipients to Remove ({recipientsToRemove.length})
                  </h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-32 overflow-y-auto">
                    <div className="flex flex-wrap gap-2">
                      {recipientsToRemove.map((email, index) => (
                        <div key={index} className="flex items-center space-x-2 bg-white border border-gray-300 rounded-lg px-3 py-1">
                          <span className="text-sm text-gray-700">{email}</span>
                          <button
                            onClick={() => {
                              setRecipientsToRemove(prev => prev.filter((_, i) => i !== index));
                            }}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowRemoveRecipients(false);
                    setNewRecipient('');
                    setCsvData('');
                    setRecipientsToRemove([]);
                  }}
                  className="px-4 py-2 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (recipientsToRemove.length > 0) {
                      setIsAddingRecipient(true);
                      try {
                        const response = await apiService.bulkRemoveRecipients(Array.from(selectedItems), recipientsToRemove.join(','));

                        if (response.success_count > 0) {
                          toast.success(
                            `Recipients removed successfully from ${response.unique_files_affected || response.success_count} file${(response.unique_files_affected || response.success_count) > 1 ? 's' : ''}!`,
                            {
                              duration: 4000,
                            }
                          );

                          // Invalidate queries to refresh data
                          queryClient.invalidateQueries({ queryKey: ['files'] });
                          queryClient.invalidateQueries({ queryKey: ['shared-files'] });
                        } else {
                          // No recipients were actually removed
                          toast.error(
                            'No recipients found with the provided email addresses',
                            {
                              duration: 4000,
                            }
                          );
                        }

                        // Only show error toast if there were actual errors (not just "recipient not found" which is expected)
                        if (response.error_count > 0 && response.success_count === 0) {
                          toast.error(
                            `Failed to remove recipients from ${response.error_count} file${response.error_count > 1 ? 's' : ''}`,
                            {
                              duration: 4000,
                            }
                          );
                        }

                        // Close modal and reset
                        setShowRemoveRecipients(false);
                        setNewRecipient('');
                        setCsvData('');
                        setRecipientsToRemove([]);
                      } catch (error: any) {
                        console.error('Error removing recipients:', error);
                        toast.error(error.response?.data?.error || 'Failed to remove recipients', {
                          duration: 4000,
                        });
                      } finally {
                        setIsAddingRecipient(false);
                      }
                    }
                  }}
                  disabled={recipientsToRemove.length === 0 || isAddingRecipient}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 rounded-lg"
                >
                  {isAddingRecipient ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Removing...</span>
                    </>
                  ) : (
                    <>
                      <TrashIcon className="w-4 h-4" />
                      <span>Remove Recipients</span>
                    </>
                  )}
                  {recipientsToRemove.length > 0 && (
                    <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                      {recipientsToRemove.length}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Processing Recipients Modal */}
      {
        isProcessingRecipients && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Adding Recipients</h3>
                  <p className="text-sm text-gray-500">Processing recipient additions...</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-700">
                  Please wait while we add the recipients to your selected files.
                </p>
              </div>
            </div>
          </div>
        )
      }

      {/* Change Policy Modal */}
      {
        showChangePolicy && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
            onClick={() => {
              setShowChangePolicy(false);
              setSelectedPolicyId('');
              setSelectedFolderPolicyId('');
              setOverwriteRecipients(false);
            }}
          >
            <div
              className="bg-white rounded-lg p-6 w-full max-w-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Change Policy</h3>
                <button
                  onClick={() => {
                    setShowChangePolicy(false);
                    setSelectedPolicyId('');
                    setSelectedFolderPolicyId('');
                    setOverwriteRecipients(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Change policy for {selectedItems.size} selected asset{selectedItems.size > 1 ? 's' : ''}.
                  {selectedAssetsByType.files.length > 0 && (
                    <span className="block mt-1">
                      • {selectedAssetsByType.files.length} file{selectedAssetsByType.files.length > 1 ? 's' : ''}
                    </span>
                  )}
                  {selectedAssetsByType.folders.length > 0 && (
                    <span className="block mt-1">
                      • {selectedAssetsByType.folders.length} folder{selectedAssetsByType.folders.length > 1 ? 's' : ''}
                    </span>
                  )}
                </p>
              </div>

              <div className="space-y-6">
                {/* File Policy Selection */}
                {selectedAssetsByType.files.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select policy for {selectedAssetsByType.files.length} file{selectedAssetsByType.files.length > 1 ? 's' : ''}
                    </label>
                    {policiesLoading ? (
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                        <span className="text-sm text-gray-500">Loading policies...</span>
                      </div>
                    ) : (
                      <div>
                        <select
                          value={selectedPolicyId}
                          onChange={(e) => setSelectedPolicyId(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                        >
                          <option value="">Select a file policy...</option>
                          {filePolicies.map((policy: any) => (
                            <option key={policy.id} value={policy.id}>
                              {policy.name} - {policy.description}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {/* Folder Policy Selection */}
                {selectedAssetsByType.folders.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select policy for {selectedAssetsByType.folders.length} folder{selectedAssetsByType.folders.length > 1 ? 's' : ''}
                    </label>
                    {policiesLoading ? (
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                        <span className="text-sm text-gray-500">Loading policies...</span>
                      </div>
                    ) : (
                      <div>
                        <select
                          value={selectedFolderPolicyId}
                          onChange={(e) => setSelectedFolderPolicyId(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                        >
                          <option value="">Select a folder policy...</option>
                          {folderPolicies.map((policy: any) => (
                            <option key={policy.id} value={policy.id}>
                              {policy.name} - {policy.description}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {/* Recipients Overwrite Option */}
                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={overwriteRecipients}
                      onChange={(e) => setOverwriteRecipients(e.target.checked)}
                      className="h-4 w-4 text-governate-600 focus:ring-governate-500 border-gray-300 rounded"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                        Overwrite recipients with those defined in policy
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        This will replace existing file recipients with the predefined recipients defined in the policy.
                      </p>
                    </div>
                  </label>
                </div>

              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowChangePolicy(false);
                    setSelectedPolicyId('');
                    setSelectedFolderPolicyId('');
                    setOverwriteRecipients(false);
                  }}
                  className="px-4 py-2 border-2 border-black text-black text-sm font-medium bg-white hover:bg-gray-50 transition-colors rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    const result = await handlePolicyChange();
                    if (result.success) {
                      // Show success toast
                      const totalAssets = selectedAssetsByType.files.length + selectedAssetsByType.folders.length;
                      const assetTypeText = selectedAssetsByType.files.length > 0 && selectedAssetsByType.folders.length > 0
                        ? 'assets'
                        : selectedAssetsByType.files.length > 0
                          ? 'files'
                          : 'folders';

                      toast.success(
                        `Policy for ${totalAssets} selected ${assetTypeText} updated successfully!`,
                        {
                          duration: 4000,
                          style: {
                            background: '#10B981',
                            color: 'white',
                          },
                        }
                      );

                      setShowChangePolicy(false);
                      setSelectedPolicyId('');
                      setSelectedFolderPolicyId('');
                      setOverwriteRecipients(false);
                      // Refresh the files data
                      window.location.reload();
                    } else {
                      // Show error toast
                      toast.error(result.error || 'Failed to change policy', {
                        duration: 4000,
                      });
                    }
                  }}
                  disabled={(!selectedPolicyId && selectedAssetsByType.files.length > 0) || (!selectedFolderPolicyId && selectedAssetsByType.folders.length > 0) || isChangingPolicy}
                  className="px-4 py-2 border-2 border-black text-black text-sm font-medium bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 rounded-lg"
                >
                  {isChangingPolicy ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                      <span>Changing Policy...</span>
                    </>
                  ) : (
                    <>
                      <CogIcon className="w-4 h-4" />
                      <span>Change Policy</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Change Owner Modal */}
      {
        showChangeOwner && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
            onClick={() => {
              setShowChangeOwner(false);
              setNewOwnerEmail('');
            }}
          >
            <div
              className="bg-white rounded-lg p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Change Owner</h3>
                <button
                  onClick={() => {
                    setShowChangeOwner(false);
                    setNewOwnerEmail('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Change owner for {selectedItems.size} selected file{selectedItems.size > 1 ? 's' : ''}.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Owner Email
                  </label>
                  <input
                    type="email"
                    value={newOwnerEmail}
                    onChange={(e) => setNewOwnerEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                    placeholder="Enter new owner's email address"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowChangeOwner(false);
                    setNewOwnerEmail('');
                  }}
                  className="px-4 py-2 border-2 border-black text-black text-sm font-medium bg-white hover:bg-gray-50 transition-colors rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (newOwnerEmail.trim()) {
                      setIsChangingOwner(true);
                      try {
                        const result = await apiService.changeOwner(Array.from(selectedItems), newOwnerEmail);

                        if (result.success_count > 0) {
                          toast.success(
                            `Owner changed successfully for ${result.success_count} file${result.success_count > 1 ? 's' : ''}!`,
                            {
                              duration: 4000,
                              style: {
                                background: '#10B981',
                                color: 'white',
                              },
                            }
                          );

                          setShowChangeOwner(false);
                          setNewOwnerEmail('');
                          // Refresh the files data
                          window.location.reload();
                        }

                        if (result.error_count > 0) {
                          toast.error(
                            `Failed to change owner for ${result.error_count} file${result.error_count > 1 ? 's' : ''}. Check access permissions.`,
                            {
                              duration: 5000,
                            }
                          );
                        }

                        if (result.success_count === 0 && result.error_count === 0) {
                          toast.error('No files were processed', {
                            duration: 4000,
                          });
                        }
                      } catch (error: any) {
                        toast.error(error.response?.data?.error || 'Failed to change owner', {
                          duration: 4000,
                        });
                      } finally {
                        setIsChangingOwner(false);
                      }
                    }
                  }}
                  disabled={!newOwnerEmail.trim() || isChangingOwner}
                  className="px-4 py-2 border-2 border-black text-black text-sm font-medium bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 rounded-lg"
                >
                  {isChangingOwner ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                      <span>Changing Owner...</span>
                    </>
                  ) : (
                    <>
                      <UserIcon className="w-4 h-4" />
                      <span>Change Owner</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Block Confirmation Modal */}
      {
        showBlockConfirm && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
            onClick={() => setShowBlockConfirm(false)}
          >
            <div
              className="bg-white rounded-lg p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 border-2 border-yellow-600 rounded-full flex items-center justify-center">
                  <ShieldExclamationIcon className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Block File Access</h3>
                  <p className="text-sm text-gray-500">Temporarily block access to selected files</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-700">
                  Are you sure you want to block access to <span className="font-semibold">{selectedItems.size}</span> file{selectedItems.size > 1 ? 's' : ''}?
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Recipients will not be able to access these files until you unblock them. You can unblock them later at any time.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowBlockConfirm(false)}
                  disabled={isBlockingFiles}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkBlockFiles}
                  disabled={isBlockingFiles}
                  className="flex-1 px-4 py-2 bg-white text-yellow-600 border-2 border-yellow-600 rounded-lg hover:bg-yellow-50 disabled:opacity-50 transition-colors flex items-center justify-center space-x-2"
                >
                  {isBlockingFiles ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600"></div>
                      <span>Blocking...</span>
                    </>
                  ) : (
                    <>
                      <LockClosedIcon className="w-4 h-4" />
                      <span>Block</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Unblock Confirmation Modal */}
      {
        showUnblockConfirm && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
            onClick={() => setShowUnblockConfirm(false)}
          >
            <div
              className="bg-white rounded-lg p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 border-2 border-green-600 rounded-full flex items-center justify-center">
                  <LockOpenIcon className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Unblock File Access</h3>
                  <p className="text-sm text-gray-500">Restore access to selected files</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-700">
                  Are you sure you want to unblock access to <span className="font-semibold">{selectedItems.size}</span> file{selectedItems.size > 1 ? 's' : ''}?
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Recipients will regain access to these files according to their permission settings.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowUnblockConfirm(false)}
                  disabled={isBlockingFiles}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkUnblockFiles}
                  disabled={isBlockingFiles}
                  className="flex-1 px-4 py-2 bg-white text-green-600 border-2 border-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50 transition-colors flex items-center justify-center space-x-2"
                >
                  {isBlockingFiles ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                      <span>Unblocking...</span>
                    </>
                  ) : (
                    <>
                      <LockOpenIcon className="w-4 h-4" />
                      <span>Unblock</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Single File Block/Unblock Confirmation Modal */}
      {
        showSingleToggleConfirm && singleToggleFile && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
            onClick={() => {
              setShowSingleToggleConfirm(false);
              setSingleToggleFile(null);
            }}
          >
            <div
              className="bg-white rounded-lg p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className={`w-10 h-10 border-2 rounded-full flex items-center justify-center ${singleToggleFile.status === 'blocked'
                  ? 'border-green-600'
                  : 'border-yellow-600'
                  }`}>
                  {singleToggleFile.status === 'blocked' ? (
                    <LockOpenIcon className="w-6 h-6 text-green-600" />
                  ) : (
                    <LockClosedIcon className="w-6 h-6 text-yellow-600" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {singleToggleFile.status === 'blocked' ? 'Unblock' : 'Block'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {singleToggleFile.status === 'blocked'
                      ? 'Allow recipients to access this file'
                      : 'Temporarily prevent access to this file'
                    }
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-700 mb-2">
                  File: <span className="font-semibold">{singleToggleFile.name}</span>
                </p>
                <div className={`mt-3 p-3 border rounded-lg ${singleToggleFile.status === 'blocked'
                  ? 'bg-green-50 border-green-200'
                  : 'bg-yellow-50 border-yellow-200'
                  }`}>
                  <p className={`text-sm font-medium ${singleToggleFile.status === 'blocked' ? 'text-green-800' : 'text-yellow-800'
                    }`}>
                    {singleToggleFile.status === 'blocked' ? '✓ What happens:' : '⚠️ What happens:'}
                  </p>
                  <ul className={`text-sm mt-1 ml-4 list-disc ${singleToggleFile.status === 'blocked' ? 'text-green-700' : 'text-yellow-700'
                    }`}>
                    {singleToggleFile.status === 'blocked' ? (
                      <>
                        <li>Recipients can access the file again</li>
                        <li>All existing permissions will be restored</li>
                        <li>Access logs will continue tracking</li>
                      </>
                    ) : (
                      <>
                        <li>Recipients will be unable to access this file</li>
                        <li>Existing permissions are preserved</li>
                        <li>You can unblock it anytime</li>
                      </>
                    )}
                  </ul>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowSingleToggleConfirm(false);
                    setSingleToggleFile(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSingleToggleBlock}
                  className={`flex-1 px-4 py-2 bg-white border-2 rounded-lg transition-colors flex items-center justify-center space-x-2 ${singleToggleFile.status === 'blocked'
                    ? 'text-green-600 border-green-600 hover:bg-green-50'
                    : 'text-yellow-600 border-yellow-600 hover:bg-yellow-50'
                    }`}
                >
                  {singleToggleFile.status === 'blocked' ? (
                    <>
                      <LockOpenIcon className="w-4 h-4" />
                      <span>Unblock</span>
                    </>
                  ) : (
                    <>
                      <LockClosedIcon className="w-4 h-4" />
                      <span>Block</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Delete Confirmation Modal */}
      {
        showDeleteConfirm && createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
            onClick={() => setShowDeleteConfirm(false)}
          >
            <div
              className="bg-white rounded-lg p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 border-2 border-red-600 rounded-full flex items-center justify-center">
                  <TrashIcon className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Permanently</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-700">
                  Are you sure you want to permanently delete <span className="font-semibold">{selectedItems.size}</span> asset{selectedItems.size > 1 ? 's' : ''}?
                </p>
                <div className="mt-3 p-3 bg-white border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium">⚠️ Warning:</p>
                  <ul className="text-sm text-red-700 mt-1 ml-4 list-disc">
                    <li>Asset{selectedItems.size > 1 ? 's' : ''} will be marked as deleted and record will be preserved</li>
                    <li>Decryption key{selectedItems.size > 1 ? 's' : ''} will be deleted from storage</li>
                    <li>All recipients will lose access immediately</li>
                    <li>This action cannot be reversed</li>
                  </ul>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeletingFiles}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkDeleteFiles}
                  disabled={isDeletingFiles}
                  className="flex-1 px-4 py-2 bg-white text-red-600 border-2 border-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors flex items-center justify-center space-x-2"
                >
                  {isDeletingFiles ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <TrashIcon className="w-4 h-4" />
                      <span>Delete</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      }

      {/* Manage Tags Modal */}
      {
        showManageTags && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
            onClick={() => {
              setShowManageTags(false);
              setTagInput('');
              setTagsToManage([]);
              setTagOperation('add');
            }}
          >
            <div
              className="bg-white dark:bg-gray-900 rounded-lg p-6 w-full max-w-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Manage Tags</h3>
                <button
                  onClick={() => {
                    setShowManageTags(false);
                    setTagInput('');
                    setTagsToManage([]);
                    setTagOperation('add');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Manage tags for {selectedItems.size} selected asset{selectedItems.size > 1 ? 's' : ''}.
                </p>
              </div>

              {/* Operation Toggle */}
              <div className="flex space-x-2 mb-6">
                <button
                  onClick={() => setTagOperation('add')}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tagOperation === 'add'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                >
                  Add Tags
                </button>
                <button
                  onClick={() => setTagOperation('remove')}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tagOperation === 'remove'
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                >
                  Remove Tags
                </button>
              </div>

              {/* Tag Input */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Enter tags (one per line or comma-separated)
                  </label>
                  <div className="flex space-x-2 relative">
                    <div className="flex-1 relative">
                      <input
                        ref={tagInputRef}
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();

                            // If a suggestion is selected, use it
                            if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < tagSuggestions.length) {
                              const selectedTag = tagSuggestions[selectedSuggestionIndex];
                              setTagsToManage(prev => {
                                if (!prev.includes(selectedTag)) {
                                  return [...prev, selectedTag];
                                }
                                return prev;
                              });
                              setTagInput('');
                              setShowTagDropdown(false);
                              setSelectedSuggestionIndex(-1);
                            } else if (tagInput.trim()) {
                              // Otherwise, add the typed tag(s)
                              const tags = tagInput.split(',').map(t => t.trim()).filter(t => t);
                              setTagsToManage(prev => {
                                const combined = [...prev, ...tags];
                                return Array.from(new Set(combined));
                              });
                              setTagInput('');
                              setShowTagDropdown(false);
                              setSelectedSuggestionIndex(-1);
                            }
                          } else if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            if (showTagDropdown && tagSuggestions.length > 0) {
                              setSelectedSuggestionIndex(prev =>
                                prev < tagSuggestions.length - 1 ? prev + 1 : prev
                              );
                            }
                          } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            if (showTagDropdown && tagSuggestions.length > 0) {
                              setSelectedSuggestionIndex(prev =>
                                prev > 0 ? prev - 1 : -1
                              );
                            }
                          } else if (e.key === 'Escape') {
                            setShowTagDropdown(false);
                            setSelectedSuggestionIndex(-1);
                          }
                        }}
                        onFocus={() => {
                          if (tagSuggestions.length > 0) {
                            setShowTagDropdown(true);
                          }
                        }}
                        placeholder="e.g., financial, legal, confidential"
                        className="w-full px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                      />
                      {isSearchingTags && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                        </div>
                      )}

                      {/* Autocomplete Dropdown */}
                      {showTagDropdown && tagSuggestions.length > 0 && (
                        <div className="tag-suggestions-dropdown absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {tagSuggestions.map((suggestion, index) => (
                            <button
                              key={index}
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault(); // Prevent input blur
                                setTagsToManage(prev => {
                                  if (!prev.includes(suggestion)) {
                                    return [...prev, suggestion];
                                  }
                                  return prev;
                                });
                                setTagInput('');
                                setShowTagDropdown(false);
                                setSelectedSuggestionIndex(-1);
                                setTimeout(() => tagInputRef.current?.focus(), 0);
                              }}
                              className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center space-x-2 ${index === selectedSuggestionIndex
                                ? 'bg-blue-100 dark:bg-blue-900 text-gray-900 dark:text-white'
                                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white'
                                }`}
                            >
                              <TagIcon className="w-4 h-4 text-gray-400" />
                              <span>{suggestion}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        if (tagInput.trim()) {
                          const tags = tagInput.split(',').map(t => t.trim()).filter(t => t);
                          setTagsToManage(prev => {
                            const combined = [...prev, ...tags];
                            return Array.from(new Set(combined));
                          });
                          setTagInput('');
                          setShowTagDropdown(false);
                        }
                      }}
                      disabled={!tagInput.trim()}
                      className="p-2 border-2 border-black dark:border-white text-black dark:text-white text-sm font-medium bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-lg"
                      title="Add tag"
                    >
                      <PlusIcon className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {tagSuggestions.length > 0 ? 'Select from suggestions or press Enter to add new tag.' : 'Press Enter or click + to add. Type to search existing tags.'}
                  </p>
                </div>

                {/* Tags to Manage List */}
                {tagsToManage.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tags to {tagOperation} ({tagsToManage.length})
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 max-h-32 overflow-y-auto">
                      <div className="flex flex-wrap gap-2">
                        {tagsToManage.map((tag, index) => (
                          <div key={index} className="flex items-center space-x-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1">
                            <TagIcon className="w-3 h-3 text-gray-400" />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{tag}</span>
                            <button
                              onClick={() => {
                                setTagsToManage(prev => prev.filter((_, i) => i !== index));
                              }}
                              className="text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowManageTags(false);
                    setTagInput('');
                    setTagsToManage([]);
                    setTagOperation('add');
                  }}
                  className="px-4 py-2 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (tagsToManage.length > 0) {
                      setIsManagingTags(true);
                      try {
                        if (tagOperation === 'add') {
                          const response = await apiService.bulkAddAssetTags(
                            Array.from(selectedItems),
                            tagsToManage,
                            'user'
                          );

                          toast.success(
                            `Tags successfully added to ${response.success_count} asset${response.success_count > 1 ? 's' : ''}!`,
                            { duration: 4000 }
                          );
                        } else {
                          const response = await apiService.bulkRemoveAssetTags(
                            Array.from(selectedItems),
                            tagsToManage
                          );

                          toast.success(
                            `Tags successfully removed from ${response.success_count} asset${response.success_count > 1 ? 's' : ''}!`,
                            { duration: 4000 }
                          );
                        }

                        // Close modal and reset
                        setShowManageTags(false);
                        setTagInput('');
                        setTagsToManage([]);
                        setTagOperation('add');

                        // Refresh files data
                        queryClient.invalidateQueries({ queryKey: ['files'] });
                      } catch (error: any) {
                        console.error('Error managing tags:', error);
                        toast.error(error.response?.data?.error || 'Failed to manage tags', { duration: 4000 });
                      } finally {
                        setIsManagingTags(false);
                      }
                    }
                  }}
                  disabled={tagsToManage.length === 0 || isManagingTags}
                  className={`px-4 py-2 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 rounded-lg ${tagOperation === 'add' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'
                    }`}
                >
                  {isManagingTags ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      {tagOperation === 'add' ? <PlusIcon className="w-4 h-4" /> : <TrashIcon className="w-4 h-4" />}
                      <span>{tagOperation === 'add' ? 'Add Tags' : 'Remove Tags'}</span>
                    </>
                  )}
                  {tagsToManage.length > 0 && (
                    <span className={`text-xs px-2 py-1 rounded-full ${tagOperation === 'add' ? 'bg-blue-500' : 'bg-red-500'
                      }`}>
                      {tagsToManage.length}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Review/Approve Modal */}
      {
        showReviewModal && selectedRequest && createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-75 flex items-center justify-center z-[9999] backdrop-blur-sm"
            onClick={() => setShowReviewModal(false)}
          >
            <div
              className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Review Access Request</h3>
                <button
                  onClick={() => setShowReviewModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto px-6 py-4 space-y-4">
                {/* Compact summary card */}
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700/60 overflow-hidden">
                  {/* File + requester */}
                  <div className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">File</p>
                      <div className="flex items-center gap-2">
                        <DocumentIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {formatFileName(selectedRequest.file_name || 'Unknown File', selectedRequest.asset_type, selectedRequest.metadata?.mime_type)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                        {requestsSubTab === 'received' ? 'Requester' : 'File Owner'}
                      </p>
                      <div className="flex items-center gap-2">
                        <UserIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
                          {requestsSubTab === 'received'
                            ? (selectedRequest.requester_email || 'Unknown')
                            : (selectedRequest.owner_email || 'Unknown')}
                          {requestsSubTab === 'received' && selectedRequest.requester_name && ` · ${selectedRequest.requester_name}`}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Type + access info */}
                  <div className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Request Type</p>
                      <div className="flex items-center gap-2">
                        {selectedRequest.request_type === 'access_period_limit'
                          ? <ClockIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          : <HashtagIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {getRequestTypeLabel(selectedRequest.request_type)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Access</p>
                      {selectedRequest.request_type === 'access_count_limit' ? (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {selectedRequest.current_times_accessible
                              ? `${selectedRequest.current_total_accesses} / ${selectedRequest.current_times_accessible}`
                              : `${selectedRequest.current_total_accesses} / ∞`}
                          </span>
                          {selectedRequest.requested_times_accessible && (
                            <>
                              <span className="text-gray-400">→</span>
                              <span className="font-medium text-indigo-600 dark:text-indigo-400">{selectedRequest.requested_times_accessible}</span>
                            </>
                          )}
                        </div>
                      ) : selectedRequest.request_type === 'access_period_limit' ? (
                        <div className="text-sm">
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {selectedRequest.current_allowed_access_to ? formatDate(selectedRequest.current_allowed_access_to) : 'No time limit'}
                          </span>
                          {selectedRequest.requested_allowed_access_to && (
                            <div className="text-indigo-600 dark:text-indigo-400 text-xs mt-0.5">
                              → {formatDate(selectedRequest.requested_allowed_access_to)}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-700 dark:text-gray-300">Custom permissions requested</span>
                      )}
                    </div>
                  </div>

                  {/* Message */}
                  {(selectedRequest.reason || selectedRequest.requester_message) && (
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/40">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Message</p>
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedRequest.requester_message || selectedRequest.reason}</p>
                    </div>
                  )}
                </div>

                {/* Response Form (only for pending requests in received tab) */}
                {selectedRequest.status === 'pending' && requestsSubTab === 'received' && (
                  <div className="space-y-4">
                    {/* Override fields (only shown when accepting) */}
                    {responseStatus === 'accepted' && (
                      <>
                        {selectedRequest.request_type === 'access_count_limit' && (
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                              Override Access Count (optional)
                            </label>
                            <input
                              type="number"
                              value={overrideTimesAccessible}
                              onChange={(e) => setOverrideTimesAccessible(e.target.value)}
                              placeholder={selectedRequest.requested_times_accessible?.toString() || 'Unlimited'}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Leave empty to set unlimited ({selectedRequest.requested_times_accessible ? `requested: ${selectedRequest.requested_times_accessible}` : 'Unlimited'})
                            </p>
                          </div>
                        )}

                        {selectedRequest.request_type === 'access_period_limit' && (
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                              Override Access Until (optional)
                            </label>
                            <div className="flex items-center gap-3">
                              <div className="flex-1 relative">
                                <DateTimePicker
                                  value={isUnlimitedPeriod ? null : overrideAllowedAccessTo}
                                  onChange={(value) => {
                                    if (value) {
                                      setOverrideAllowedAccessTo(value);
                                      setIsUnlimitedPeriod(false);
                                    } else {
                                      setOverrideAllowedAccessTo('');
                                      setIsUnlimitedPeriod(true);
                                    }
                                  }}
                                  placeholder={isUnlimitedPeriod || !overrideAllowedAccessTo ? 'Unlimited' : 'yyyy-mm-dd hh:mm:ss'}
                                  disabled={isUnlimitedPeriod}
                                  minDate={new Date()}
                                  showTimeSelect={true}
                                  timeIntervals={1}
                                />
                                {!isUnlimitedPeriod && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Format: yyyy-mm-dd hh:mm:ss (e.g., 2029-10-17 18:39:00)
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">Unlimited</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newUnlimited = !isUnlimitedPeriod;
                                    setIsUnlimitedPeriod(newUnlimited);
                                    if (newUnlimited) {
                                      setOverrideAllowedAccessTo('');
                                    } else {
                                      // Restore to current_allowed_access_to when toggling off
                                      if (selectedRequest.current_allowed_access_to) {
                                        const date = new Date(selectedRequest.current_allowed_access_to);
                                        const year = date.getFullYear();
                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                        const day = String(date.getDate()).padStart(2, '0');
                                        const hours = String(date.getHours()).padStart(2, '0');
                                        const minutes = String(date.getMinutes()).padStart(2, '0');
                                        const seconds = String(date.getSeconds()).padStart(2, '0');
                                        setOverrideAllowedAccessTo(`${year}-${month}-${day} ${hours}:${minutes}:${seconds}`);
                                      } else {
                                        setOverrideAllowedAccessTo('');
                                      }
                                    }
                                  }}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${isUnlimitedPeriod ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                                    }`}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isUnlimitedPeriod ? 'translate-x-6' : 'translate-x-1'
                                      }`}
                                  />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Response Message (optional)
                      </label>
                      <textarea
                        value={responseMessage}
                        onChange={(e) => setResponseMessage(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                        placeholder="Add a message to the requester..."
                      />
                    </div>
                  </div>
                )}

                {/* View only for my requests or already responded */}
                {selectedRequest.status !== 'pending' && (
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Response</p>
                    <div className="flex items-center gap-3 mb-2">{getStatusBadge(selectedRequest.status)}</div>
                    {selectedRequest.responded_at && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">{formatDate(selectedRequest.responded_at)}</p>
                    )}
                    {selectedRequest.response_message && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{selectedRequest.response_message}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Footer with action buttons */}
              {selectedRequest.status === 'pending' && requestsSubTab === 'received' && (
                <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-end gap-3">
                  <button
                    onClick={() => setShowReviewModal(false)}
                    disabled={isResponding}
                    className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleRespond('rejected')}
                    disabled={isResponding}
                    className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center gap-2"
                  >
                    {isResponding && responseStatus === 'rejected' ? (
                      <><div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-gray-500"></div><span>Declining...</span></>
                    ) : (
                      <><XMarkIcon className="w-4 h-4" /><span>Decline</span></>
                    )}
                  </button>
                  <button
                    onClick={() => handleRespond('accepted')}
                    disabled={isResponding}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                  >
                    {isResponding && responseStatus === 'accepted' ? (
                      <><div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div><span>Approving...</span></>
                    ) : (
                      <><CheckIcon className="w-4 h-4" /><span>Approve</span></>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>,
          document.body
        )
      }

      {/* Reject Confirmation Modal */}
      {
        showRejectConfirmModal && requestToReject && createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-75 flex items-center justify-center z-[9999] backdrop-blur-sm"
            onClick={() => {
              setShowRejectConfirmModal(false);
              setRequestToReject(null);
            }}
          >
            <div
              className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Reject Access Request</h3>
                <button
                  onClick={() => {
                    setShowRejectConfirmModal(false);
                    setRequestToReject(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  Are you sure you want to reject this access request?
                </p>
                {requestToReject.file_name && (
                  <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">File:</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{formatFileName(requestToReject.file_name, requestToReject.asset_type, requestToReject.metadata?.mime_type)}</div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-800/50 flex space-x-3">
                <button
                  onClick={() => {
                    setShowRejectConfirmModal(false);
                    setRequestToReject(null);
                  }}
                  disabled={isRejecting}
                  className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectConfirm}
                  disabled={isRejecting}
                  className="flex-1 px-4 py-2.5 rounded-lg text-white font-medium bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center space-x-2"
                >
                  {isRejecting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Rejecting...</span>
                    </>
                  ) : (
                    <>
                      <XMarkIcon className="w-4 h-4" />
                      <span>Reject Request</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      }

      {/* Cancel Confirmation Modal */}
      {
        showCancelConfirmModal && createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-75 flex items-center justify-center z-[9999] backdrop-blur-sm"
            onClick={() => {
              setShowCancelConfirmModal(false);
              setRequestToCancel(null);
            }}
          >
            <div
              className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Cancel Access Request</h3>
                <button
                  onClick={() => {
                    setShowCancelConfirmModal(false);
                    setRequestToCancel(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Are you sure you want to cancel this access request? This action cannot be undone.
                </p>
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-800/50 flex space-x-3">
                <button
                  onClick={() => {
                    setShowCancelConfirmModal(false);
                    setRequestToCancel(null);
                  }}
                  disabled={isCanceling}
                  className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
                >
                  No
                </button>
                <button
                  onClick={handleCancelConfirm}
                  disabled={isCanceling}
                  className="flex-1 px-4 py-2.5 rounded-lg text-white font-medium bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center space-x-2"
                >
                  {isCanceling ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Canceling...</span>
                    </>
                  ) : (
                    <>
                      <XMarkIcon className="w-4 h-4" />
                      <span>Yes</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      }

      {/* Request Access Modal */}
      {
        showRequestAccessModal && selectedFileForRequest && createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
            onClick={() => setShowRequestAccessModal(false)}
          >
            <div
              className="bg-white rounded-lg p-6 w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Request Access Change</h3>
                <button
                  onClick={() => setShowRequestAccessModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    File
                  </label>
                  <div className="flex items-center space-x-2 bg-gray-50 rounded-lg p-3">
                    <DocumentIcon className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-900">{selectedFileForRequest.name}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Request Type
                  </label>
                  <select
                    value={requestType}
                    onChange={(e) => {
                      setRequestType(e.target.value as any);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                  >
                    <option value="access_count_limit">Increase number of times I can access</option>
                    <option value="access_period_limit">Extend the accessible period</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason (optional)
                  </label>
                  <textarea
                    value={requestReason}
                    onChange={(e) => setRequestReason(e.target.value)}
                    rows={3}
                    placeholder="Explain why you need this access change..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => setShowRequestAccessModal(false)}
                    disabled={isCreatingRequest}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (!selectedFileForRequest) return;

                      setIsCreatingRequest(true);
                      try {
                        await apiService.createAccessRequest(
                          selectedFileForRequest.id,
                          requestType,
                          requestReason || undefined
                        );

                        toast.success('Access request created successfully');
                        setShowRequestAccessModal(false);
                        setSelectedFileForRequest(null);
                        queryClient.invalidateQueries({ queryKey: ['access-requests'] });
                      } catch (error: any) {
                        toast.error(error.response?.data?.error || 'Failed to create access request');
                      } finally {
                        setIsCreatingRequest(false);
                      }
                    }}
                    disabled={isCreatingRequest}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center space-x-2"
                  >
                    {isCreatingRequest ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Creating...</span>
                      </>
                    ) : (
                      <>
                        <PaperAirplaneIcon className="w-4 h-4" />
                        <span>Send Request</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )
      }

      {/* Share modal (recipient adding another recipient when asset has Share permission) */}
      {
        showShareModal && selectedFileForShare && createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
            onClick={() => setShowShareModal(false)}
          >
            <div
              className="bg-white dark:bg-gray-900 rounded-lg p-6 w-full max-w-lg shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('table.shareAsset')}</h3>
                <button
                  onClick={() => setShowShareModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Add a recipient to this asset. Restrictions are applied by the asset owner&apos;s company.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Asset</label>
                  <div className="flex items-center space-x-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                    <DocumentIcon className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-900 dark:text-white truncate">{selectedFileForShare.name}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Recipient email</label>
                  <input
                    type="email"
                    value={shareRecipientEmail}
                    onChange={(e) => setShareRecipientEmail(e.target.value)}
                    placeholder="email@example.com"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => setShowShareModal(false)}
                    disabled={isAddingShareRecipient}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (!selectedFileForShare || !shareRecipientEmail.trim()) return;
                      const email = shareRecipientEmail.trim();
                      if (!isValidEmail(email)) {
                        toast.error(t('validEmail'));
                        return;
                      }
                      setIsAddingShareRecipient(true);
                      try {
                        await apiService.addRecipient(selectedFileForShare.id, {
                          type: 'email',
                          value: email,
                          is_external: true,
                        });
                        toast.success('Recipient added');
                        setShareRecipientEmail('');
                        setShowShareModal(false);
                        setSelectedFileForShare(null);
                        queryClient.invalidateQueries({ queryKey: ['shared-with-me'] });
                      } catch (error: any) {
                        const msg = error.response?.data?.error || error.message || 'Failed to add recipient';
                        toast.error(msg);
                      } finally {
                        setIsAddingShareRecipient(false);
                      }
                    }}
                    disabled={isAddingShareRecipient || !shareRecipientEmail.trim()}
                    className="flex-1 px-4 py-2 bg-governate-600 text-white rounded-lg hover:bg-governate-700 disabled:opacity-50 transition-colors flex items-center justify-center space-x-2"
                  >
                    {isAddingShareRecipient ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Adding...</span>
                      </>
                    ) : (
                      <>
                        <UserGroupIcon className="w-4 h-4" />
                        <span>Add recipient</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )
      }

      {/* Global Search Modal */}
      {isSearchModalOpen && (
        <GlobalSearchModal
          isOpen={isSearchModalOpen}
          onClose={() => setIsSearchModalOpen(false)}
          initialTab="assets"
          singleTabMode={true}
          initialFilterValues={(() => {
            // Only read from localStorage if we're on the assets page
            if (location.pathname === '/assets') {
              try {
                const saved = localStorage.getItem('assets_currentFilters');
                if (saved) return JSON.parse(saved);
              } catch (e) {
                console.error('Failed to parse saved filters:', e);
              }
            }
            // Fallback to current filters state
            const hasActiveFilters = searchTerm || emailSearch || tags.length > 0 || dateFrom || dateTo;
            if (!hasActiveFilters) return undefined;
            return {
              assetName: searchTerm,
              emailSearch: emailSearch,
              assetTags: tags,
              dateFrom: dateFrom,
              dateTo: dateTo,
            };
          })()}
        />
      )}
    </div >
  );
};

export default Assets;
